import { useEffect, useState, useCallback, createContext, useContext, useMemo, useRef } from "react";
import axios from "axios";
import DOMPurify from "dompurify";
import { BrowserRouter, Routes, Route, Link, useNavigate, useParams } from "react-router-dom";
import {
  PawPrint, Sun, Cloud, CloudRain, CloudSnow, Wind, Droplets, Thermometer,
  Search, Filter, Shuffle, Heart, Printer, Sparkles, Moon, LogIn, LogOut,
  UserPlus, X, Play, ChevronRight, ChevronDown, Calendar, Award, Activity, Loader2, AlertCircle, Zap, Target, Users, BookOpen, LayoutGrid,
  Share2, Plus, Trash2, CalendarDays, Wand2, Check, Copy, ChevronLeft, Menu, Video, ExternalLink, Info
} from "lucide-react";
import "./index.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

// DOMPurify config: allow the tags/attrs we intentionally embed from the original coaches' HTML.
// The source HTML is trusted (extracted from the coaches' own file at build time) but sanitizing
// belt-and-suspenders eliminates any XSS risk if the source is ever modified or user-supplied content
// leaks in via the AI Coach or Create Game flow.
const SANITIZE_OPTS = {
  ADD_ATTR: ["target", "rel"],
  FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "form", "input"],
  FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onfocus", "onblur", "onchange", "onsubmit"],
};
const cleanHtml = (html) => DOMPurify.sanitize(html || "", SANITIZE_OPTS);

const AuthCtx = createContext(null);
const useAuth = () => useContext(AuthCtx);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  useEffect(() => {
    axios.get(`${API}/auth/me`).then(r => setUser(r.data)).catch(() => setUser(null)).finally(() => setChecking(false));
  }, []);
  const toggleFav = async (gameId) => {
    const { data } = await axios.post(`${API}/favorites/toggle`, { game_id: gameId });
    setUser(u => u ? { ...u, favorites: data.favorites } : u);
  };
  return <AuthCtx.Provider value={{ user, setUser, checking, toggleFav }}>{children}</AuthCtx.Provider>;
}

const fmtErr = d => !d ? "Error" : typeof d === "string" ? d : Array.isArray(d) ? d.map(x => x.msg || JSON.stringify(x)).join(" ") : JSON.stringify(d);

// ---------- THEME ----------
function useTheme() {
  const [dark, setDark] = useState(() => localStorage.getItem("theme") === "dark");
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);
  return [dark, setDark];
}

// ---------- NAV ----------
function Nav({ dark, setDark }) {
  const { user, setUser } = useAuth();
  const [authOpen, setAuthOpen] = useState(null);
  const [sectionsOpen, setSectionsOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sections, setSections] = useState([]);
  const ddRef = useRef(null);
  useEffect(() => { axios.get(`${API}/sections`).then(r=>setSections(r.data)).catch(()=>{}); }, []);
  useEffect(() => {
    const onClick = e => { if (ddRef.current && !ddRef.current.contains(e.target)) setSectionsOpen(false); };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);
  const logout = async () => { await axios.post(`${API}/auth/logout`); setUser(null); setMobileOpen(false); };
  const links = [
    { to: "/", label: "Home", testid: "nav-home-link" },
    { to: "/games", label: "Games Library", testid: "nav-games" },
    { to: "/weeks", label: "Weeks", testid: "nav-weeks" },
    { to: "/brain-breaks", label: "Brain Breaks", testid: "nav-brain-breaks" },
    { to: "/ai", label: "AI Coach", testid: "nav-ai" },
    { to: "/weather", label: "Weather", testid: "nav-weather" },
    { to: "/favorites", label: "My Favorites", testid: "nav-favs" },
    { to: "/planner", label: "Planner", testid: "nav-planner", authOnly: true },
    { to: "/about", label: "About", testid: "nav-about" },
  ];
  const closeMobile = () => setMobileOpen(false);
  return (
    <>
      <nav className="sticky top-0 z-40 border-b-2 border-black dark:border-white no-print" style={{ background: "var(--surface)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <Link to="/" data-testid="nav-home" className="flex items-center gap-2 group">
            <div className="bg-royal text-white p-1.5 border-2 border-black dark:border-white" style={{background:"var(--royal)"}}>
              <PawPrint strokeWidth={2.5} size={22} />
            </div>
            <div className="font-display font-black text-2xl sm:text-3xl tracking-tight leading-none italic">
              PANTHERS<span style={{color:"var(--royal)"}}>PE</span>
            </div>
          </Link>
          <div className="hidden xl:flex items-center gap-1 font-display uppercase tracking-wider text-xs font-bold">
            {links.filter(l => !l.authOnly || user).map(l => (
              <Link key={l.to} to={l.to} data-testid={l.testid} className="px-2.5 py-2 hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black">{l.label}</Link>
            ))}
            <div ref={ddRef} className="relative">
              <button data-testid="nav-sections-toggle" onClick={()=>setSectionsOpen(o=>!o)} className="px-2.5 py-2 flex items-center gap-1 hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black">
                Sections <ChevronDown size={14}/>
              </button>
              {sectionsOpen && (
                <div className="absolute top-full right-0 mt-1 w-80 border-2 border-black dark:border-white shadow-hard-lg z-50 max-h-[70vh] overflow-y-auto" style={{background:"var(--surface)"}}>
                  {sections.map(s => (
                    <Link key={s.slug} to={`/sections/${s.slug}`} data-testid={`nav-section-${s.slug}`} onClick={()=>setSectionsOpen(false)}
                      className="block px-3 py-2 text-sm border-b last:border-0 hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black" style={{borderColor:"var(--border)"}}>
                      <span className="mr-2">{s.emoji}</span>{s.title}
                      <span className="ml-2 chip chip-blue !text-[0.6rem] !py-0 !px-1.5">{s.card_count}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
            {user && <Link to="/create" data-testid="nav-create" className="px-2.5 py-2 hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black">+ Create</Link>}
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="theme-toggle" onClick={() => setDark(!dark)} className="p-2 border-2 border-black dark:border-white hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black" aria-label="toggle theme">
              {dark ? <Sun size={18}/> : <Moon size={18}/>}
            </button>
            {user ? (
              <>
                <span className="hidden md:inline font-display uppercase text-sm font-bold">{user.name}</span>
                <button data-testid="logout-btn" onClick={logout} className="btn-sec text-sm !py-2 !px-3"><LogOut size={16}/></button>
              </>
            ) : (
              <>
                <button data-testid="open-login" onClick={() => setAuthOpen("login")} className="btn-sec text-sm !py-2 !px-3 hidden md:flex items-center gap-1"><LogIn size={16}/> Login</button>
                <button data-testid="open-signup" onClick={() => setAuthOpen("signup")} className="btn-pri text-sm !py-2 !px-3 flex items-center gap-1"><UserPlus size={16}/> <span className="hidden sm:inline">Sign Up</span></button>
              </>
            )}
            <button data-testid="mobile-menu-toggle" onClick={()=>setMobileOpen(o=>!o)} className="xl:hidden p-2 border-2 border-black dark:border-white" aria-label="menu">
              {mobileOpen ? <X size={18}/> : <Menu size={18}/>}
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="xl:hidden border-t-2 border-black dark:border-white max-h-[80vh] overflow-y-auto" style={{background:"var(--surface)"}} data-testid="mobile-menu">
            <div className="px-4 py-3 space-y-1 font-display uppercase text-sm font-bold tracking-wider">
              {links.filter(l => !l.authOnly || user).map(l => (
                <Link key={l.to} to={l.to} onClick={closeMobile} data-testid={`mobile-${l.testid}`} className="block px-2 py-2 border-b hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black" style={{borderColor:"var(--border)"}}>{l.label}</Link>
              ))}
              {user && <Link to="/create" onClick={closeMobile} className="block px-2 py-2 border-b" style={{borderColor:"var(--border)"}}>+ Create Game</Link>}
              <div className="pt-2 border-t-2 mt-2" style={{borderColor:"var(--border)"}}>
                <div className="text-xs mb-1" style={{color:"var(--muted)"}}>Sections</div>
                {sections.map(s => (
                  <Link key={s.slug} to={`/sections/${s.slug}`} onClick={closeMobile} className="block px-2 py-1.5 text-xs">
                    <span className="mr-1">{s.emoji}</span>{s.title}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </nav>
      {authOpen && <AuthModal mode={authOpen} onClose={() => setAuthOpen(null)} switchMode={m => setAuthOpen(m)}/>}
    </>
  );
}

function AuthModal({ mode, onClose, switchMode }) {
  const { setUser } = useAuth();
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [err, setErr] = useState(""); const [loading, setLoading] = useState(false);
  const submit = async e => {
    e.preventDefault(); setLoading(true); setErr("");
    try {
      const url = mode === "login" ? "/auth/login" : "/auth/register";
      const body = mode === "login" ? { email: form.email, password: form.password } : form;
      const { data } = await axios.post(`${API}${url}`, body);
      setUser(data); onClose();
    } catch (e) { setErr(fmtErr(e.response?.data?.detail) || "Failed"); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="auth-modal">
      <div className="card p-7 max-w-md w-full" onClick={e => e.stopPropagation()} style={{background:"var(--surface)"}}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-3xl font-black uppercase tracking-tight">{mode === "login" ? "Coach Login" : "Join the Team"}</h2>
          <button onClick={onClose} data-testid="close-auth"><X size={22}/></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          {mode === "signup" && <input data-testid="auth-name" required placeholder="Your Name" className="input" value={form.name} onChange={e=>setForm({...form,name:e.target.value})}/>}
          <input data-testid="auth-email" required type="email" placeholder="Email" className="input" value={form.email} onChange={e=>setForm({...form,email:e.target.value})}/>
          <input data-testid="auth-password" required type="password" placeholder="Password (min 6)" className="input" value={form.password} onChange={e=>setForm({...form,password:e.target.value})}/>
          {err && <div className="flex gap-2 items-center text-sm p-2 border-2" style={{borderColor:"var(--alert)",color:"var(--alert)"}}><AlertCircle size={16}/>{err}</div>}
          <button data-testid="auth-submit" disabled={loading} className="btn-pri w-full flex items-center justify-center gap-2">
            {loading && <Loader2 className="animate-spin" size={16}/>}
            {mode === "login" ? "Login" : "Create Account"}
          </button>
        </form>
        <p className="mt-4 text-sm text-center" style={{color:"var(--muted)"}}>
          {mode === "login" ? "New here? " : "Have an account? "}
          <button onClick={() => switchMode(mode === "login" ? "signup" : "login")} className="font-bold underline" style={{color:"var(--royal)"}}>
            {mode === "login" ? "Sign up" : "Login"}
          </button>
        </p>
      </div>
    </div>
  );
}

// ---------- WEATHER WIDGET ----------
function WeatherWidget({ compact = false }) {
  const [w, setW] = useState(null); const [err, setErr] = useState("");
  useEffect(() => { axios.get(`${API}/weather`).then(r=>setW(r.data)).catch(e=>setErr("Weather unavailable")); }, []);
  if (err) return <div className="card p-4 text-sm" style={{background:"var(--surface)"}}><AlertCircle className="inline mr-2" size={14}/>{err}</div>;
  if (!w) return <div className="card p-4 animate-pulse h-32" style={{background:"var(--surface)"}}/>;
  const Icon = w.current.code === 0 ? Sun : w.current.code < 50 ? Cloud : w.current.code < 70 ? CloudRain : CloudSnow;
  const chipCls = w.recommendation.color === "success" ? "chip-success" : w.recommendation.color === "alert" ? "chip-alert" : "chip-blue";
  return (
    <div className="card p-5" style={{background:"var(--surface)"}} data-testid="weather-widget">
      <div className="flex items-center justify-between mb-2">
        <span className="label" style={{color:"var(--muted)"}}>Live · {w.location}</span>
        <span className={`chip ${chipCls}`}>{w.recommendation.status}</span>
      </div>
      <div className="flex items-end gap-4 mb-3">
        <Icon size={56} strokeWidth={2} style={{color:"var(--royal)"}}/>
        <div>
          <div className="font-display font-black text-6xl leading-none">{w.current.temp}<span className="text-3xl align-top">°F</span></div>
          <div className="font-display uppercase tracking-wider text-sm font-bold" style={{color:"var(--muted)"}}>{w.current.label}</div>
        </div>
      </div>
      <div className="flex gap-4 text-sm mb-3 font-bold">
        <span className="flex items-center gap-1"><Droplets size={14}/>{w.current.humidity}%</span>
        <span className="flex items-center gap-1"><Wind size={14}/>{w.current.wind} mph</span>
      </div>
      <div className="border-t-2 pt-3 text-sm" style={{borderColor:"var(--text)"}}>
        <strong className="font-display uppercase tracking-wider text-xs block mb-1" style={{color:"var(--royal)"}}>PE Plan</strong>
        {w.recommendation.tip}
      </div>
      {!compact && (
        <div className="grid grid-cols-5 gap-1 mt-4 pt-3 border-t-2" style={{borderColor:"var(--text)"}}>
          {w.forecast.map((d,i) => (
            <div key={i} className="text-center p-1.5 border" style={{borderColor:"var(--border)"}}>
              <div className="label text-[0.6rem]" style={{color:"var(--muted)"}}>{new Date(d.date).toLocaleDateString("en-US",{weekday:"short"})}</div>
              <div className="font-display font-black text-lg">{d.high}°</div>
              <div className="text-xs" style={{color:"var(--muted)"}}>{d.low}°</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- GAME CARD ----------
function GameCard({ g }) {
  const { user, toggleFav } = useAuth();
  const isFav = user?.favorites?.includes(g.id);
  const catColor = { "Movement Skills":"chip-blue", "Cooperation":"chip-success", "Listening Skills":"chip-blue",
    "Character Focus":"chip-alert", "Cross-Curricular":"chip-blue", "Adapted/Inclusive":"chip-blue" }[g.category] || "chip-blue";
  const coverSrc = g.cover_url ? `${process.env.REACT_APP_BACKEND_URL}${g.cover_url}` : null;
  return (
    <Link to={`/games/${g.id}`} data-testid={`game-card-${g.id}`} className="card block p-0 overflow-hidden" style={{background:"var(--surface)"}}>
      {coverSrc && (
        <div className="w-full aspect-[16/9] overflow-hidden" style={{background:"var(--bg)"}}>
          <img src={coverSrc} alt={g.name} loading="lazy" className="w-full h-full object-cover"/>
        </div>
      )}
      <div className="p-4 text-white relative" style={{background:"var(--royal)"}}>
        <div className="flex items-start justify-between mb-2">
          <span className="chip bg-white text-black border-white">{g.grade}</span>
          {user && (
            <button onClick={e=>{e.preventDefault();e.stopPropagation();toggleFav(g.id);}} data-testid={`fav-${g.id}`} className="p-1">
              <Heart size={18} fill={isFav?"#fff":"none"} strokeWidth={2.5}/>
            </button>
          )}
        </div>
        <h3 className="font-display font-black text-2xl uppercase leading-tight tracking-tight">{g.name}</h3>
        <div className="flex gap-1 mt-1">{Array.from({length:g.engagement||3}).map((_,i)=><Zap key={i} size={12} fill="#FBBF24" strokeWidth={0}/>)}</div>
      </div>
      <div className="p-4 space-y-2 text-sm">
        <p className="line-clamp-3" style={{color:"var(--muted)"}}>{g.description}</p>
        <div className="flex flex-wrap gap-1.5 pt-2">
          <span className={`chip ${catColor}`}>{g.category}</span>
          <span className="chip">{g.time}</span>
        </div>
        {g.creator_name && (
          <div className="pt-1 text-xs" style={{color:"var(--muted)"}} data-testid={`game-creator-${g.id}`}>
            Created by <span className="font-bold" style={{color:"var(--royal)"}}>{g.creator_name}</span>
          </div>
        )}
      </div>
    </Link>
  );
}

// ---------- HOME ----------
// ---------- HOME ----------
function GlobalSpotlight() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf = 0;
    const onMove = (e) => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        el.style.setProperty("--mx", `${e.clientX}px`);
        el.style.setProperty("--my", `${e.clientY}px`);
        el.style.opacity = "1";
      });
    };
    const onLeave = () => { el.style.opacity = "0"; };
    window.addEventListener("mousemove", onMove);
    document.addEventListener("mouseleave", onLeave);
    return () => {
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseleave", onLeave);
      cancelAnimationFrame(raf);
    };
  }, []);
  return <div ref={ref} className="global-spotlight" aria-hidden="true"/>;
}

function PanthersCrest() {
  return (
    <div className="panthers-crest absolute -top-6 -right-6 sm:top-6 sm:right-6 pointer-events-none select-none" aria-hidden="true">
      <div className="crest-ring">
        <svg viewBox="0 0 200 200" className="crest-svg">
          <defs>
            {/* Top arc: left to right curving over the top of the circle */}
            <path id="crest-text-top" d="M 22,100 A 78,78 0 0,1 178,100"/>
            {/* Bottom arc: right to left curving along the bottom (so text reads right-side up) */}
            <path id="crest-text-bot" d="M 178,100 A 78,78 0 0,1 22,100"/>
          </defs>
          <circle cx="100" cy="100" r="92" fill="none" stroke="#FBBF24" strokeWidth="4"/>
          <circle cx="100" cy="100" r="78" fill="none" stroke="#FFFFFF" strokeWidth="2"/>
          <text fill="#FBBF24" fontSize="18" fontFamily="'Barlow Condensed', sans-serif" fontWeight="900" letterSpacing="2">
            <textPath href="#crest-text-top" startOffset="50%" textAnchor="middle">VAN ALSTYNE</textPath>
          </text>
          <text fill="#FFFFFF" fontSize="14" fontFamily="'Barlow Condensed', sans-serif" fontWeight="900" letterSpacing="3">
            <textPath href="#crest-text-bot" startOffset="50%" textAnchor="middle">★ EST · K-4 PE ★</textPath>
          </text>
        </svg>
        <div className="crest-paw">
          <svg viewBox="0 0 64 64" width="68" height="68">
            {/* White backing circle so paw pops against any background */}
            <circle cx="32" cy="32" r="30" fill="#FFFFFF"/>
            {/* Black panther paw */}
            <ellipse cx="32" cy="40" rx="13" ry="10" fill="#0A0A0A"/>
            <ellipse cx="15" cy="26" rx="4.5" ry="6.5" fill="#0A0A0A"/>
            <ellipse cx="24" cy="18" rx="4.5" ry="6.5" fill="#0A0A0A"/>
            <ellipse cx="40" cy="18" rx="4.5" ry="6.5" fill="#0A0A0A"/>
            <ellipse cx="49" cy="26" rx="4.5" ry="6.5" fill="#0A0A0A"/>
          </svg>
        </div>
      </div>
    </div>
  );
}

function Home() {
  const [stats, setStats] = useState({});
  const [gotd, setGotd] = useState(null);
  useEffect(() => {
    axios.get(`${API}/stats`).then(r=>setStats(r.data));
    axios.get(`${API}/games/random`).then(r=>setGotd(r.data));
  }, []);
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden border-b-4 border-black dark:border-white" style={{background:"var(--royal)"}}>
        {/* Baseball field background */}
        <div className="absolute inset-0 pointer-events-none" style={{backgroundImage:"url('https://images.pexels.com/photos/1308713/pexels-photo-1308713.jpeg?auto=compress&cs=tinysrgb&w=1920')", backgroundSize:"cover", backgroundPosition:"center", opacity:0.5}}/>
        {/* Royal blue tint overlay */}
        <div className="absolute inset-0 pointer-events-none" style={{background:"linear-gradient(135deg, rgba(30,64,175,0.78) 0%, rgba(30,58,138,0.55) 45%, rgba(10,10,10,0.75) 100%)"}}/>
        {/* Panthers crest */}
        <PanthersCrest/>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 text-white">
          <div className="inline-flex items-center gap-2 mb-4 chip bg-white text-black border-white"><Activity size={14}/> K-4 PHYSICAL EDUCATION</div>
          <h1 className="font-display font-black text-5xl sm:text-7xl lg:text-8xl uppercase leading-[0.9] tracking-tighter italic mb-4">
            Van Alstyne<br/><span style={{color:"#FBBF24"}}>Panthers</span> PE
          </h1>
          <p className="text-xl sm:text-2xl max-w-2xl font-bold mb-8">The championship-level curriculum hub. 850+ entries across 13 sections — TEKS-aligned games, FitnessGram, Adapted PE, lesson plans, Field Day, and more. Built by coaches, for coaches.</p>
          <div className="flex flex-wrap gap-3">
            <Link to="/games" data-testid="hero-browse" className="btn-pri !bg-white !text-black !border-white" style={{boxShadow:"4px 4px 0 0 #FBBF24"}}>Browse Playbook <ChevronRight className="inline" size={18}/></Link>
            <Link to="/ai" data-testid="hero-ai" className="btn-sec !text-white !border-white"><Sparkles className="inline mr-1" size={16}/> AI Coach</Link>
          </div>
        </div>
        <div className="scan-line"/>
      </section>

      {/* STATS BAR */}
      <section className="border-b-2 border-black dark:border-white" style={{background:"var(--obsidian, #0A0A0A)", color:"#fff"}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[["Games", stats.total_games||"—", Target],
            ["Sections", stats.sections||"—", LayoutGrid],
            ["Total Entries", stats.total_cards||"—", BookOpen],
            ["Teachers", stats.teachers||"—", Users]].map(([l,v,I],i) => (
            <div key={i} className="flex items-center gap-3 border-l-4 pl-3" style={{borderColor:"#3B82F6"}}>
              <I size={28} strokeWidth={2.5} style={{color:"#3B82F6"}}/>
              <div>
                <div className="font-display font-black text-3xl leading-none">{v}</div>
                <div className="label" style={{color:"#A3A3A3"}}>{l}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-6 flex flex-wrap gap-3 justify-center sm:justify-start">
          <Link to="/games" data-testid="stats-cta-games" className="btn-pri !bg-white !text-black !border-white !text-sm"><LayoutGrid size={14} className="inline mr-1"/>Browse All Games</Link>
          <Link to="/brain-breaks" data-testid="stats-cta-brain" className="btn-sec !text-white !border-white !text-sm"><Video size={14} className="inline mr-1"/>Brain Breaks</Link>
        </div>
      </section>

      {/* GAME OF DAY + WEATHER */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid lg:grid-cols-2 gap-6">
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-black text-3xl uppercase tracking-tight flex items-center gap-2"><Calendar size={24}/> Game of the Day</h2>
            <button data-testid="shuffle-gotd" onClick={()=>axios.get(`${API}/games/shuffle`).then(r=>setGotd(r.data))} className="btn-sec !text-xs !py-2 !px-3"><Shuffle size={14} className="inline mr-1"/>Shuffle</button>
          </div>
          {gotd && <GameCard g={gotd}/>}
        </div>
        <div>
          <h2 className="font-display font-black text-3xl uppercase tracking-tight mb-4 flex items-center gap-2"><Sun size={24}/> Today&apos;s Forecast</h2>
          <WeatherWidget/>
        </div>
      </section>

      {/* SMART DAILY PICKER */}
      <SmartPicker/>

      {/* CTA */}
      <section className="border-t-2 border-black dark:border-white py-12" style={{background:"var(--surface)"}}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <Sparkles size={40} strokeWidth={2.5} className="mx-auto mb-3" style={{color:"var(--royal)"}}/>
          <h2 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight mb-3">Need a custom game?</h2>
          <p className="text-lg mb-5" style={{color:"var(--muted)"}}>Tell our AI Coach the grade, equipment, and theme. Get a complete 5E lesson plan with TEKS in seconds.</p>
          <Link to="/ai" data-testid="cta-ai" className="btn-pri">Generate a Game</Link>
        </div>
      </section>
    </div>
  );
}

// ---------- GAMES LIBRARY ----------
function Games() {
  const [list, setList] = useState([]); const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(""); const [cat, setCat] = useState("All"); const [grade, setGrade] = useState("All");
  const [selected, setSelected] = useState(new Set());
  const [packetMode, setPacketMode] = useState(false);
  const fetch = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/games`, { params: { q: q || undefined, category: cat, grade } })
      .then(r => setList(r.data)).finally(() => setLoading(false));
  }, [q, cat, grade]);
  useEffect(() => { const t = setTimeout(fetch, 300); return () => clearTimeout(t); }, [fetch]);
  const cats = ["All","Movement Skills","Cooperation","Listening Skills","Character Focus","Cross-Curricular","Adapted/Inclusive"];
  const grades = ["All","K","1st","2nd","3rd","4th","K-4","K-5"];
  
  const toggleSel = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  };
  
  const printPacket = async () => {
    if (selected.size === 0) { alert("Select at least one game"); return; }
    const ids = [...selected];
    const cards = await Promise.all(ids.map(id => axios.get(`${API}/cards/${id}`).then(r=>r.data).catch(()=>null)));
    const html = `<html><head><title>Panthers PE Packet</title>
      <style>
        body{font-family:'DM Sans',sans-serif;color:#0A0A0A;background:white;padding:20px;}
        h1{font-family:'Barlow Condensed',sans-serif;font-weight:900;text-transform:uppercase;font-style:italic;color:#1E40AF;font-size:2.5rem;border-bottom:4px solid #1E40AF;}
        .game{page-break-after:always;border:2px solid #0A0A0A;padding:20px;margin-bottom:20px;}
        .game-header{background:#1E40AF;color:white;padding:15px;margin:-20px -20px 15px;}
        h2{font-family:'Barlow Condensed',sans-serif;font-weight:900;text-transform:uppercase;font-size:1.8rem;margin:0;}
        .meta{display:flex;gap:8px;flex-wrap:wrap;font-size:0.75rem;font-weight:700;margin-top:5px;}
        .meta span{background:white;color:black;padding:2px 8px;border:1px solid white;}
        h3{font-family:'Barlow Condensed',sans-serif;text-transform:uppercase;color:#1E40AF;}
        table{border-collapse:collapse;width:100%;}td,th{border:1px solid #ccc;padding:5px 8px;}
        @page { margin: 1.5cm; }
      </style></head><body>
      <h1>Panthers PE — Lesson Packet</h1>
      <p>${cards.length} games · Generated ${new Date().toLocaleDateString()}</p>
      ${cards.filter(Boolean).map(c => `
        <div class="game">
          <div class="game-header">
            <h2>${c.emoji||""} ${c.name}</h2>
            <div class="meta"><span>${c.grade}</span><span>${c.category}</span><span>${c.time}</span></div>
          </div>
          <p><strong>Description:</strong> ${c.description||""}</p>
          <p><strong>Equipment:</strong> ${c.equipment||""}</p>
          <p><strong>Character Focus:</strong> ${c.character_focus||""}</p>
          ${(c.teks||[]).length ? '<h3>TEKS</h3><ul>'+c.teks.map(t=>'<li>'+t+'</li>').join("")+'</ul>' : ''}
          ${Object.keys(c.five_e||{}).filter(k=>c.five_e[k]).length ? '<h3>5E Lesson</h3>'+Object.entries(c.five_e).filter(([_,v])=>v).map(([k,v])=>'<p><strong>'+k.toUpperCase()+':</strong> '+v+'</p>').join("") : ''}
          ${Object.keys(c.modifications||{}).length ? '<h3>Modifications</h3><table>'+Object.entries(c.modifications).map(([k,v])=>'<tr><th>'+k+'</th><td>'+v+'</td></tr>').join("")+'</table>' : ''}
        </div>
      `).join("")}
      </body></html>`;
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, "packet");
    if (win) {
      win.addEventListener("load", () => { win.print(); }, { once: true });
      // Revoke the URL after the window has had a chance to load and print
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    }
  };
  
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-display font-black text-5xl uppercase tracking-tighter italic">The <span style={{color:"var(--royal)"}}>Playbook</span></h1>
        <div className="flex gap-2 items-center">
          <span className="chip chip-blue">{list.length} GAMES</span>
          <button data-testid="packet-mode" onClick={()=>{setPacketMode(!packetMode);setSelected(new Set());}} className={`btn-sec !text-xs !py-2 !px-3 ${packetMode?"!bg-black !text-white dark:!bg-white dark:!text-black":""}`}>
            {packetMode ? "Done" : <><Printer size={12} className="inline mr-1"/>Build Packet</>}
          </button>
          {packetMode && selected.size > 0 && (
            <button data-testid="packet-print" onClick={printPacket} className="btn-pri !text-xs !py-2 !px-3">
              <Printer size={12} className="inline mr-1"/>Print {selected.size} Game{selected.size>1?"s":""}
            </button>
          )}
        </div>
      </div>
      <div className="card p-4 mb-6 space-y-3" style={{background:"var(--surface)"}}>
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2" style={{color:"var(--muted)"}}/>
          <input data-testid="search-input" value={q} onChange={e=>setQ(e.target.value)} placeholder="Search games, skills, character focus..." className="input pl-10"/>
        </div>
        <div className="flex flex-wrap gap-2">
          {cats.map(c => <button key={c} data-testid={`cat-${c}`} onClick={()=>setCat(c)} className={`chip cursor-pointer ${cat===c?"chip-blue":""}`}>{c}</button>)}
        </div>
        <div className="flex flex-wrap gap-2">
          {grades.map(g => <button key={g} data-testid={`grade-${g}`} onClick={()=>setGrade(g)} className={`chip cursor-pointer ${grade===g?"chip-blue":""}`}>{g}</button>)}
        </div>
      </div>
      {loading ? <div className="text-center py-12"><Loader2 className="animate-spin inline" size={40}/></div> :
        list.length === 0 ? <div className="text-center py-12" style={{color:"var(--muted)"}}>No games match. Try clearing filters.</div> :
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {list.map(g => packetMode ? (
            <div key={g.id} className="relative">
              <button onClick={()=>toggleSel(g.id)} data-testid={`select-${g.id}`} className="absolute top-2 right-2 z-20 w-8 h-8 border-2 border-black dark:border-white flex items-center justify-center" style={{background: selected.has(g.id) ? "var(--royal)" : "var(--surface)", color: selected.has(g.id) ? "white" : "var(--text)"}}>
                {selected.has(g.id) ? <Check size={16} strokeWidth={3}/> : ""}
              </button>
              <div className={selected.has(g.id) ? "ring-4" : ""} style={{boxShadow: selected.has(g.id) ? "0 0 0 4px var(--royal)" : "none"}}><GameCard g={g}/></div>
            </div>
          ) : <GameCard key={g.id} g={g}/>)}
        </div>}
    </div>
  );
}

function ShareBtn({ id }) {
  const [copied, setCopied] = useState(false);
  const share = async () => {
    const url = `${window.location.origin}/content/${id}`;
    try {
      if (navigator.share) { await navigator.share({ title: "Panthers PE Lesson", url }); }
      else { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(()=>setCopied(false), 2000); }
    } catch (err) { console.error("Share failed:", err); /* user canceled or clipboard blocked */ }
  };
  return (
    <button data-testid="share-btn" onClick={share} className="btn-sec !text-xs !py-2 !px-3" title="Copy share link">
      {copied ? <><Check size={14} className="inline mr-1"/>Copied!</> : <><Share2 size={14} className="inline mr-1"/>Share</>}
    </button>
  );
}

function CoverManager({ gameId, coverUrl, onChange }) {
  const [options, setOptions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [regenBusy, setRegenBusy] = useState(false);
  const [hint, setHint] = useState("");
  const generate = async () => {
    setBusy(true); setOptions([]);
    try {
      const { data } = await axios.post(`${API}/games/${gameId}/cover/generate`, { extra_hint: hint || null });
      setOptions(data.options || []);
    } catch (e) { alert(fmtErr(e.response?.data?.detail) || "Generation failed"); }
    finally { setBusy(false); }
  };
  const regenerate = async () => {
    setRegenBusy(true); setOptions([]);
    try {
      const { data } = await axios.post(`${API}/games/${gameId}/cover/regenerate`);
      setOptions(data.options || []);
    } catch (e) { alert(fmtErr(e.response?.data?.detail) || "Regenerate failed"); }
    finally { setRegenBusy(false); }
  };
  const pick = async (url) => {
    await axios.patch(`${API}/games/${gameId}/cover`, { cover_url: url });
    onChange(url);
    setOptions([]);
  };
  const remove = async () => {
    if (!window.confirm("Remove the cover photo?")) return;
    await axios.patch(`${API}/games/${gameId}/cover`, { cover_url: "" });
    onChange(null);
  };
  return (
    <div className="card p-4 mb-6 no-print" style={{background:"var(--surface)", borderTop:"4px solid var(--royal)"}} data-testid="cover-manager">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div>
          <div className="label" style={{color:"var(--royal)"}}>Cover Photo</div>
          <p className="text-xs" style={{color:"var(--muted)"}}>{coverUrl ? "Regenerate, replace or remove the current cover." : "Generate a set of AI cover options matched to this game."}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {coverUrl && (
            <button
              type="button"
              data-testid="cover-regenerate"
              onClick={regenerate}
              disabled={regenBusy || busy}
              className="btn-sec !text-xs !py-1.5 !px-3 disabled:opacity-50"
              title="Make more like this"
            >
              {regenBusy ? <><Loader2 className="animate-spin inline" size={12}/> Working…</> : <><Copy size={12} className="inline mr-1"/>Make More Like This</>}
            </button>
          )}
          <button
            type="button"
            data-testid="cover-generate"
            onClick={generate}
            disabled={busy || regenBusy}
            className="btn-pri !text-xs !py-1.5 !px-3 disabled:opacity-50"
          >
            {busy ? <><Loader2 className="animate-spin inline" size={12}/> Generating…</> : <><Sparkles size={12} className="inline mr-1"/>{coverUrl ? "New" : "Generate"} Options</>}
          </button>
          {coverUrl && (
            <button
              type="button"
              data-testid="cover-remove"
              onClick={remove}
              className="btn-sec !text-xs !py-1.5 !px-3"
              style={{borderColor:"var(--alert)",color:"var(--alert)"}}
            ><X size={12} className="inline mr-1"/>Remove</button>
          )}
        </div>
      </div>
      <input
        data-testid="cover-hint"
        className="input !text-xs mb-2"
        placeholder="Optional hint — e.g., 'outdoor grass field', 'winter theme', 'basketball court'"
        value={hint}
        onChange={e=>setHint(e.target.value)}
      />
      {options.length > 0 && (
        <>
          <div className="label mb-1" style={{color:"var(--royal)"}}>Pick one</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {options.map((url, i) => (
              <button
                type="button"
                key={url}
                data-testid={`cover-option-${i}`}
                onClick={()=>pick(url)}
                className="block p-0 border-2 border-black hover:border-royal overflow-hidden text-left"
              >
                <img src={`${process.env.REACT_APP_BACKEND_URL}${url}`} alt={`Option ${i+1}`} className="w-full aspect-[16/9] object-cover"/>
                <div className="p-1 text-xs text-center font-bold" style={{background:"var(--royal)",color:"#fff"}}>Use this</div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}


// ---------- GAME DETAIL ----------
function GameDetail() {
  const { id } = useParams(); const nav = useNavigate();
  const [g, setG] = useState(null); const { user, toggleFav } = useAuth();
  useEffect(() => { axios.get(`${API}/games/${id}`).then(r=>setG(r.data)).catch(()=>nav("/games")); }, [id, nav]);
  if (!g) return <div className="text-center py-20"><Loader2 className="animate-spin inline" size={32}/></div>;
  const isFav = user?.favorites?.includes(g.id);
  const isOwner = !!(user && g.created_by && g.created_by === user.id);
  const downloadPdf = () => window.print();
  const deleteGame = async () => {
    if (!window.confirm(`DELETE the game "${g.name}"? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/games/${g.id}`);
      nav("/games");
    } catch (e) {
      alert(fmtErr(e.response?.data?.detail) || "Failed to delete");
    }
  };
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="game-detail">
      <div className="no-print mb-4 flex flex-wrap gap-2 items-center justify-between">
        <Link to="/games" className="btn-sec !text-xs !py-2 !px-3">← Back to Playbook</Link>
        <div className="flex gap-2 flex-wrap">
          {user && <button data-testid="fav-toggle" onClick={()=>toggleFav(g.id)} className="btn-sec !text-xs !py-2 !px-3"><Heart size={14} className="inline" fill={isFav?"currentColor":"none"}/> {isFav?"Saved":"Save"}</button>}
          <ShareBtn id={g.id}/>
          {isOwner && (
            <>
              <Link to={`/create?edit=${g.id}`} data-testid="edit-game-btn" className="btn-sec !text-xs !py-2 !px-3"><PawPrint size={14} className="inline mr-1"/>Edit</Link>
              <button data-testid="delete-game-btn" onClick={deleteGame} className="btn-sec !text-xs !py-2 !px-3" style={{borderColor:"var(--alert)",color:"var(--alert)"}}><Trash2 size={14} className="inline mr-1"/>Delete</button>
            </>
          )}
          <button data-testid="print-btn" onClick={downloadPdf} className="btn-pri !text-xs !py-2 !px-3"><Printer size={14} className="inline mr-1"/>Print / PDF</button>
        </div>
      </div>
      <div className="p-6 text-white mb-6" style={{background:"var(--royal)", border:"2px solid var(--text)"}}>
        {g.cover_url && (
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}${g.cover_url}`}
            alt={g.name}
            className="w-full aspect-[16/9] object-cover mb-4 border-2 border-white"
            data-testid="game-cover-hero"
          />
        )}
        <div className="flex flex-wrap gap-2 mb-3">
          <span className="chip bg-white text-black border-white">{g.grade}</span>
          <span className="chip bg-white text-black border-white">{g.category}</span>
          <span className="chip bg-white text-black border-white">{g.time}</span>
          <div className="flex gap-1 items-center">{Array.from({length:g.engagement||3}).map((_,i)=><Zap key={i} size={14} fill="#FBBF24" strokeWidth={0}/>)}</div>
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight leading-tight">{g.name}</h1>
        <p className="mt-3 text-lg">{g.description}</p>
        {g.creator_name && (
          <div className="mt-3 text-sm" data-testid="game-creator-badge">
            <span className="chip bg-white text-black border-white">
              <PawPrint size={11} className="inline mr-1"/> Created by {g.creator_name}
            </span>
          </div>
        )}
      </div>

      {g.can_manage_cover && (
        <CoverManager gameId={g.id} coverUrl={g.cover_url} onChange={(newUrl)=>setG({...g, cover_url: newUrl})}/>
      )}

      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <div className="card p-4" style={{background:"var(--surface)"}}>
          <div className="label mb-1" style={{color:"var(--royal)"}}>Equipment</div>
          <div className="font-bold">{g.equipment}</div>
        </div>
        <div className="card p-4" style={{background:"var(--surface)"}}>
          <div className="label mb-1" style={{color:"var(--royal)"}}>Character Focus</div>
          <div>{g.character_focus}</div>
        </div>
      </div>

      {(g.how_to_play && g.how_to_play.length > 0) && (
        <div className="card p-5 mb-6" style={{background:"var(--surface)"}} data-testid="how-to-play">
          <h2 className="font-display font-black text-2xl uppercase mb-3" style={{color:"var(--royal)"}}>How to Play</h2>
          <ol className="space-y-3 list-none">
            {g.how_to_play.map((s, i) => (
              <li key={i} className="flex gap-3" data-testid={`how-to-play-step-${i}`}>
                <span className="chip chip-blue shrink-0 !w-8 !h-8 flex items-center justify-center !text-sm font-black">{i+1}</span>
                <span className="pt-1">{s}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      <div className="card p-5 mb-6" style={{background:"var(--surface)"}}>
        <h2 className="font-display font-black text-2xl uppercase mb-3" style={{color:"var(--royal)"}}>TEKS Alignment</h2>
        <ul className="space-y-1">{g.teks?.map((t,i) => <li key={i} className="flex gap-2"><Target size={14} className="mt-1 shrink-0" style={{color:"var(--royal)"}}/><span>{t}</span></li>)}</ul>
        {g.cross_curricular && <div className="mt-3 pt-3 border-t-2" style={{borderColor:"var(--border)"}}><strong className="label" style={{color:"var(--royal)"}}>Cross-Curricular:</strong> {g.cross_curricular}</div>}
      </div>

      <div className="card p-5 mb-6" style={{background:"var(--surface)"}}>
        <h2 className="font-display font-black text-2xl uppercase mb-3" style={{color:"var(--royal)"}}>5E Lesson Plan</h2>
        {Object.entries(g.five_e || {}).map(([k,v]) => (
          <div key={k} className="py-2 border-b last:border-0" style={{borderColor:"var(--border)"}}>
            <div className="label" style={{color:"var(--royal)"}}>{k}</div>
            <div className="text-sm">{v}</div>
          </div>
        ))}
      </div>

      <div className="card p-5 mb-6" style={{background:"var(--surface)"}}>
        <h2 className="font-display font-black text-2xl uppercase mb-3" style={{color:"var(--royal)"}}>Grade Modifications</h2>
        {Object.entries(g.modifications || {}).map(([k,v]) => (
          <div key={k} className="py-2 border-b last:border-0 flex gap-3" style={{borderColor:"var(--border)"}}>
            <div className="chip chip-blue shrink-0">{k}</div>
            <div className="text-sm">{v}</div>
          </div>
        ))}
      </div>

      {g.youtube_url && <a href={g.youtube_url} target="_blank" rel="noopener noreferrer" data-testid="yt-link" className="btn-sec inline-flex items-center gap-2 no-print"><Play size={16}/> Watch Tutorial</a>}

      {g.html_content && (
        <details className="mt-6 card p-5" style={{background:"var(--surface)"}}>
          <summary className="cursor-pointer font-display font-black text-xl uppercase tracking-tight no-print" style={{color:"var(--royal)"}}>
            <BookOpen size={18} className="inline mr-2"/> View Original Content (full fidelity)
          </summary>
          <div className="va-html mt-4" dangerouslySetInnerHTML={{__html: cleanHtml(g.html_content)}}/>
        </details>
      )}
    </div>
  );
}

// ---------- WEEKLY LESSONS ----------
function WeeklyLessons() {
  const { user } = useAuth();
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [openWeek, setOpenWeek] = useState(null);
  const [theme, setTheme] = useState("All");
  const [myPlans, setMyPlans] = useState([]);
  const [publicPlans, setPublicPlans] = useState([]);
  const [openPublicPlan, setOpenPublicPlan] = useState(null);
  useEffect(() => {
    axios.get(`${API}/weeks`).then(r=>setWeeks(r.data)).finally(()=>setLoading(false));
  }, []);
  useEffect(() => {
    axios.get(`${API}/plans/public`).then(r=>setPublicPlans(r.data || [])).catch(()=>setPublicPlans([]));
  }, []);
  useEffect(() => {
    if (!user) { setMyPlans([]); return; }
    axios.get(`${API}/plans`).then(async r => {
      const plans = r.data || [];
      // Hydrate each plan with its lessons for previewing
      const withLessons = await Promise.all(plans.map(async p => {
        try {
          const { data } = await axios.get(`${API}/lessons`, { params: { plan_id: p.id }});
          return { ...p, lessons: data };
        } catch { return { ...p, lessons: [] }; }
      }));
      setMyPlans(withLessons);
    }).catch(()=>setMyPlans([]));
  }, [user]);
  
  const themes = useMemo(() => {
    const set = new Set(["All"]);
    weeks.forEach(w => { if (w.theme) set.add(w.theme.split("/")[0].trim().split("(")[0].trim()); });
    return [...set].slice(0, 14);
  }, [weeks]);
  
  const filtered = useMemo(() => {
    return weeks.filter(w => {
      if (theme !== "All" && !w.theme.toLowerCase().includes(theme.toLowerCase())) return false;
      if (q) {
        const ql = q.toLowerCase();
        if (!(w.title.toLowerCase().includes(ql) ||
              w.theme.toLowerCase().includes(ql) ||
              (w.games || []).some(g => g.name.toLowerCase().includes(ql)))) return false;
      }
      return true;
    });
  }, [weeks, q, theme]);
  
  if (loading) return <div className="text-center py-20"><Loader2 className="animate-spin inline" size={32}/></div>;
  
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-start gap-3 mb-2">
        <div className="text-5xl">📅</div>
        <div>
          <h1 className="font-display font-black text-3xl sm:text-5xl uppercase tracking-tighter italic leading-none">Weekly <span style={{color:"var(--royal)"}}>Lessons</span></h1>
          <p className="mt-1" style={{color:"var(--muted)"}}>{weeks.length} themed weeks · {weeks.reduce((s,w)=>s+w.games_count,0)} games · Full K-4 TEKS alignment</p>
        </div>
      </div>
      
      {/* Coach [Name]'s Weeks — user's own planner weeks */}
      {user && myPlans.length > 0 && (
        <div className="mb-8" data-testid="coach-weeks">
          <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="chip chip-blue mb-1"><PawPrint size={12} className="inline mr-1"/>YOUR PLANNER</div>
              <h2 className="font-display font-black text-2xl sm:text-3xl uppercase tracking-tight">
                Coach {((user.name || "Miller").replace(/^coach\s+/i, "").split(" ")[0]) || "Miller"}&apos;s Weeks
              </h2>
              <p className="text-sm" style={{color:"var(--muted)"}}>Pulled live from your planner. Click a week to open and edit it.</p>
            </div>
            <Link to="/planner" className="btn-sec !text-xs !py-2 !px-3"><CalendarDays size={14} className="inline mr-1"/>Open Planner</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {myPlans.map(p => {
              const byDayCount = { mon:0, tue:0, wed:0, thu:0, fri:0 };
              (p.lessons || []).forEach(l => { if (byDayCount[l.day] !== undefined) byDayCount[l.day]++; });
              const previewNames = (p.lessons || []).slice(0, 3).map(l => (l.item_meta?.title) || l.card?.name || l.card_id).filter(Boolean);
              return (
                <Link
                  key={p.id}
                  to={`/planner?plan=${p.id}`}
                  data-testid={`coach-week-${p.id}`}
                  className="card block p-0 overflow-hidden"
                  style={{background:"var(--surface)"}}
                >
                  <div className="p-4 text-white relative" style={{background:"var(--royal)"}}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="chip bg-white text-black border-white"><PawPrint size={11} className="inline mr-1"/>Your Plan</span>
                      <span className="chip bg-white text-black border-white">{p.lesson_count ?? (p.lessons||[]).length} lessons</span>
                    </div>
                    {p.is_public && (
                      <div className="mt-1">
                        <span className="chip bg-white text-black border-white !text-[0.6rem]">✓ PUBLISHED</span>
                      </div>
                    )}
                    <div className="font-display font-black text-xl uppercase tracking-tight mt-2 leading-tight">{p.name}</div>
                  </div>
                  <div className="p-3 text-sm">
                    <div className="label mb-1" style={{color:"var(--royal)"}}>Days Covered</div>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {[["mon","M"],["tue","T"],["wed","W"],["thu","Th"],["fri","F"]].map(([k,l]) => (
                        <span key={k} className={`chip !text-[0.65rem] !py-0.5 !px-1.5 ${byDayCount[k]>0 ? "chip-blue" : "opacity-30"}`}>
                          {l} {byDayCount[k]>0 ? "·"+byDayCount[k] : ""}
                        </span>
                      ))}
                    </div>
                    <div className="line-clamp-2" style={{color:"var(--muted)"}}>
                      {previewNames.length > 0 ? previewNames.join(" · ") : "Empty week — open the planner to add games."}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Coach's Public Weeks — visible to everyone except the publisher himself */}
      {publicPlans.length > 0 && user?.email !== publicPlans[0]?.owner_email && (
        <div className="mb-8" data-testid="public-coach-weeks">
          <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="chip chip-blue mb-1"><PawPrint size={12} className="inline mr-1"/>PUBLISHED WEEKS</div>
              <h2 className="font-display font-black text-2xl sm:text-3xl uppercase tracking-tight">
                Coach {((publicPlans[0]?.owner_name || "Miller").replace(/^coach\s+/i, "").split(" ")[0]) || "Miller"}&apos;s Weeks
              </h2>
              <p className="text-sm" style={{color:"var(--muted)"}}>
                Latest weekly PE plans shared by the coach. Tap a card to see the full daily breakdown.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {publicPlans.map(p => {
              const byDayCount = { mon:0, tue:0, wed:0, thu:0, fri:0 };
              (p.lessons || []).forEach(l => { if (byDayCount[l.day] !== undefined) byDayCount[l.day]++; });
              const previewNames = (p.lessons || []).slice(0, 3).map(l => (l.item_meta?.title) || l.card?.name || l.card_id).filter(Boolean);
              return (
                <button
                  key={p.id}
                  onClick={()=>setOpenPublicPlan(p)}
                  data-testid={`public-coach-week-${p.id}`}
                  className="card block p-0 overflow-hidden text-left"
                  style={{background:"var(--surface)"}}
                >
                  <div className="p-4 text-white relative" style={{background:"var(--royal)"}}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="chip bg-white text-black border-white"><PawPrint size={11} className="inline mr-1"/>Coach&apos;s Plan</span>
                      <span className="chip bg-white text-black border-white">{p.lesson_count ?? (p.lessons||[]).length} lessons</span>
                    </div>
                    <div className="font-display font-black text-xl uppercase tracking-tight mt-2 leading-tight">{p.name}</div>
                  </div>
                  <div className="p-3 text-sm">
                    <div className="label mb-1" style={{color:"var(--royal)"}}>Days Covered</div>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {[["mon","M"],["tue","T"],["wed","W"],["thu","Th"],["fri","F"]].map(([k,l]) => (
                        <span key={k} className={`chip !text-[0.65rem] !py-0.5 !px-1.5 ${byDayCount[k]>0 ? "chip-blue" : "opacity-30"}`}>
                          {l} {byDayCount[k]>0 ? "·"+byDayCount[k] : ""}
                        </span>
                      ))}
                    </div>
                    <div className="line-clamp-2" style={{color:"var(--muted)"}}>
                      {previewNames.length > 0 ? previewNames.join(" · ") : "This week is being prepared."}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="card p-4 mb-6 space-y-3 no-print" style={{background:"var(--surface)"}}>
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2" style={{color:"var(--muted)"}}/>
          <input data-testid="weeks-search" className="input pl-10" placeholder="Search week titles, themes, or games..." value={q} onChange={e=>setQ(e.target.value)}/>
        </div>
        <div className="flex flex-wrap gap-2">
          {themes.map(t => (
            <button key={t} data-testid={`weeks-theme-${t}`} onClick={()=>setTheme(t)} className={`chip cursor-pointer ${theme===t?"chip-blue":""}`}>{t}</button>
          ))}
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(w => (
          <button
            key={w.id}
            data-testid={`week-card-${w.id}`}
            onClick={()=>setOpenWeek(w.id)}
            className="card text-left p-0 block overflow-hidden"
            style={{background:"var(--surface)"}}
          >
            <div className="p-4 text-white relative" style={{background:"var(--royal)"}}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-display font-black text-5xl leading-none italic">W{w.week_num || "—"}</span>
                <span className="chip bg-white text-black border-white">{w.games_count} GAMES</span>
              </div>
              <div className="font-display font-black text-xl uppercase tracking-tight mt-2">{w.theme || "Untitled"}</div>
            </div>
            <div className="p-3 text-sm">
              <div className="label mb-1" style={{color:"var(--royal)"}}>K-4 TEKS Covered</div>
              <div className="flex flex-wrap gap-1">
                {["K","1st","2nd","3rd","4th"].map(g => (
                  <span key={g} className={`chip !text-[0.65rem] !py-0.5 !px-1.5 ${w.teks_by_grade?.[g] ? "" : "opacity-30"}`}>{g}</span>
                ))}
              </div>
              <div className="mt-2 line-clamp-2" style={{color:"var(--muted)"}}>
                {(w.games || []).slice(0,3).map(g => g.name).join(" · ") || "—"}
              </div>
            </div>
          </button>
        ))}
      </div>
      
      {openWeek && <WeekDetail weekId={openWeek} onClose={()=>setOpenWeek(null)}/>}
      {openPublicPlan && <PublicPlanDetail plan={openPublicPlan} onClose={()=>setOpenPublicPlan(null)}/>}
    </div>
  );
}

function PublicPlanDetail({ plan, onClose }) {
  const days = [
    ["mon","Monday"], ["tue","Tuesday"], ["wed","Wednesday"], ["thu","Thursday"], ["fri","Friday"]
  ];
  const lessons = plan.lessons || [];
  const byDay = { mon:[], tue:[], wed:[], thu:[], fri:[] };
  lessons.forEach(l => { if (byDay[l.day]) byDay[l.day].push(l); });
  Object.values(byDay).forEach(arr => arr.sort((a,b)=> (a.slot||0) - (b.slot||0)));

  const printPlan = () => {
    const dayHtml = days.map(([k, label]) => {
      const items = byDay[k];
      if (!items.length) return `<div class="day"><h2>${label}</h2><p class="empty">— No activities planned —</p></div>`;
      return `<div class="day"><h2>${label}</h2>` + items.map(l => {
        const title = (l.item_meta?.title) || l.card?.name || l.card_id;
        const meta = l.item_meta ? `${l.item_meta.emoji||""} ${l.item_meta.grade||""} · ${l.item_meta.duration||""} · ${l.item_meta.tag||""}` :
          l.card ? `${l.card.emoji||""} ${l.card.grade||""} · ${l.card.category||""} · ${l.card.time||""}` : "";
        return `<div class="item"><strong>${title}</strong><div class="meta">${meta}</div>${l.note?`<div class="note">Note: ${l.note}</div>`:""}</div>`;
      }).join("") + `</div>`;
    }).join("");
    const html = `<html><head><title>${plan.name}</title>
      <style>
        body{font-family:'DM Sans',sans-serif;color:#0A0A0A;background:#fff;padding:25px;line-height:1.5;}
        h1{font-family:'Barlow Condensed',sans-serif;font-weight:900;text-transform:uppercase;font-style:italic;color:#1E40AF;font-size:2.4rem;border-bottom:4px solid #1E40AF;padding-bottom:6px;}
        h2{font-family:'Barlow Condensed',sans-serif;font-weight:900;text-transform:uppercase;color:#fff;background:#1E40AF;padding:6px 12px;margin-top:22px;}
        .item{border:2px solid #0A0A0A;padding:10px 12px;margin:8px 0;break-inside:avoid;}
        .meta{font-size:0.8rem;color:#475569;margin-top:4px;}
        .note{margin-top:6px;font-size:0.85rem;color:#1E40AF;}
        .empty{color:#94A3B8;font-style:italic;}
        .day{break-inside:avoid;margin-bottom:14px;}
        @page { margin: 1.5cm; }
      </style></head><body>
      <h1>${plan.name}</h1>
      <p><em>Shared by ${plan.owner_name || "Coach"}</em></p>
      ${dayHtml}
      </body></html>`;
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, "publicplan");
    if (win) {
      win.addEventListener("load", () => { win.print(); }, { once: true });
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-2 sm:p-6 overflow-y-auto" onClick={onClose}>
      <div className="card max-w-5xl w-full my-4 max-h-[95vh] flex flex-col" onClick={e=>e.stopPropagation()} style={{background:"var(--surface)"}} data-testid="public-plan-detail">
        <div className="p-5 text-white flex items-start justify-between gap-3 sticky top-0 z-10" style={{background:"var(--royal)"}}>
          <div className="flex-1 min-w-0">
            <div className="chip bg-white text-black border-white mb-2 inline-block"><PawPrint size={11} className="inline mr-1"/>PUBLISHED WEEK</div>
            <h2 className="font-display font-black text-2xl sm:text-3xl uppercase tracking-tight">{plan.name}</h2>
            <div className="text-sm opacity-90">Shared by {plan.owner_name || "Coach"} · {lessons.length} lessons</div>
          </div>
          <div className="flex gap-2 shrink-0 no-print">
            <button data-testid="public-plan-print" onClick={printPlan} className="btn-sec !text-white !border-white !text-xs !py-2 !px-3"><Printer size={14} className="inline mr-1"/>Print</button>
            <button data-testid="public-plan-close" onClick={onClose} className="btn-sec !text-white !border-white !text-xs !py-2 !px-3"><X size={16}/></button>
          </div>
        </div>
        <div className="overflow-y-auto p-5 space-y-5">
          {days.map(([k, label]) => (
            <div key={k} data-testid={`public-plan-day-${k}`}>
              <h3 className="font-display font-black text-xl uppercase mb-2 pb-1" style={{color:"var(--royal)", borderBottom:"3px solid var(--royal)"}}>{label}</h3>
              {byDay[k].length === 0 ? (
                <p className="text-sm italic" style={{color:"var(--muted)"}}>— No activities planned —</p>
              ) : (
                <div className="space-y-2">
                  {byDay[k].map(l => {
                    const title = (l.item_meta?.title) || l.card?.name || l.card_id;
                    const emoji = l.item_meta?.emoji || l.card?.emoji || "";
                    const meta = l.item_meta
                      ? `${l.item_meta.grade || ""} · ${l.item_meta.duration || ""} · ${l.item_meta.tag || ""}`
                      : l.card
                        ? `${l.card.grade || ""} · ${l.card.category || ""} · ${l.card.time || ""}`
                        : "";
                    return (
                      <div key={l.id} className="card p-3 text-sm" style={{background:"var(--bg)"}}>
                        <div className="font-bold">{emoji} {title}</div>
                        <div className="text-xs mt-1" style={{color:"var(--muted)"}}>{meta}</div>
                        {l.note && <div className="text-xs mt-1" style={{color:"var(--royal)"}}>Note: {l.note}</div>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}



function WeekDetail({ weekId, onClose }) {
  const [w, setW] = useState(null);
  const [activeGrade, setActiveGrade] = useState("K");
  useEffect(() => {
    axios.get(`${API}/weeks/${weekId}`).then(r=>setW(r.data));
  }, [weekId]);
  
  const printWeek = () => {
    if (!w) return;
    const html = `<html><head><title>${w.title}</title>
      <style>
        body{font-family:'DM Sans',sans-serif;color:#0A0A0A;background:white;padding:25px;line-height:1.5;}
        h1{font-family:'Barlow Condensed',sans-serif;font-weight:900;text-transform:uppercase;font-style:italic;color:#1E40AF;font-size:2.4rem;border-bottom:4px solid #1E40AF;padding-bottom:6px;}
        h2{font-family:'Barlow Condensed',sans-serif;font-weight:900;text-transform:uppercase;color:#1E40AF;border-top:2px solid #1E40AF;margin-top:18px;padding-top:8px;}
        h3{font-family:'Barlow Condensed',sans-serif;font-weight:900;text-transform:uppercase;font-size:1.25rem;}
        .teks{background:#F1F5F9;padding:10px;border-left:4px solid #1E40AF;margin:6px 0;font-size:0.85rem;}
        .game{border:2px solid #0A0A0A;padding:14px;margin:14px 0;break-inside:avoid;}
        .game-head{background:#1E40AF;color:#fff;padding:8px 12px;margin:-14px -14px 10px;}
        @page { margin: 1.5cm; }
      </style></head><body>
      <h1>${w.title}</h1>
      <p><em>${w.theme || ""}</em></p>
      <h2>K-4 TEKS Alignment</h2>
      ${Object.entries(w.teks_by_grade || {}).map(([g,t]) => `<div class="teks"><strong>${g} TEKS:</strong> ${t}</div>`).join("")}
      <h2>Games & Activities (${w.games?.length || 0})</h2>
      ${(w.games || []).map(g => `
        <div class="game">
          <div class="game-head"><h3>${g.emoji||""} ${g.name}</h3><div>${g.grade} · ${g.equipment} · ${g.time}</div></div>
          <p><strong>Description:</strong> ${g.description||""}</p>
          ${g.character_focus ? `<p><strong>Character Focus:</strong> ${g.character_focus}</p>` : ""}
          ${g.five_e_html ? `<h3>5E Lesson Plan</h3>${g.five_e_html}` : ""}
        </div>
      `).join("")}
      </body></html>`;
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, "weekprint");
    if (win) {
      win.addEventListener("load", () => { win.print(); }, { once: true });
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    }
  };
  
  if (!w) return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center" onClick={onClose}>
      <Loader2 className="animate-spin text-white" size={32}/>
    </div>
  );
  
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-2 sm:p-6 overflow-y-auto" onClick={onClose}>
      <div className="card max-w-5xl w-full my-4 max-h-[95vh] flex flex-col" onClick={e=>e.stopPropagation()} style={{background:"var(--surface)"}} data-testid="week-detail">
        <div className="p-5 text-white flex items-start justify-between gap-3 sticky top-0 z-10" style={{background:"var(--royal)"}}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-display font-black text-5xl leading-none italic">W{w.week_num}</span>
              <div>
                <h2 className="font-display font-black text-2xl sm:text-3xl uppercase tracking-tight">{w.theme || w.title}</h2>
                <div className="text-sm opacity-90">{w.games_count} games · K-4 TEKS aligned</div>
              </div>
            </div>
          </div>
          <div className="flex gap-2 shrink-0 no-print">
            <button data-testid="week-print" onClick={printWeek} className="btn-sec !text-white !border-white !text-xs !py-2 !px-3"><Printer size={14} className="inline mr-1"/>Print</button>
            <button data-testid="week-close" onClick={onClose} className="btn-sec !text-white !border-white !text-xs !py-2 !px-3"><X size={16}/></button>
          </div>
        </div>
        
        <div className="overflow-y-auto p-5 space-y-5">
          {/* TEKS by grade tabs */}
          {Object.keys(w.teks_by_grade || {}).length > 0 && (
            <div>
              <h3 className="font-display font-black text-xl uppercase mb-2" style={{color:"var(--royal)"}}>TEKS Alignment</h3>
              <div className="flex flex-wrap gap-1 mb-2 no-print">
                {Object.keys(w.teks_by_grade).map(g => (
                  <button key={g} data-testid={`week-teks-${g}`} onClick={()=>setActiveGrade(g)}
                    className={`chip cursor-pointer ${activeGrade===g?"chip-blue":""}`}>{g}</button>
                ))}
              </div>
              <div className="card p-4 text-sm leading-relaxed" style={{background:"var(--bg)", borderLeft:"4px solid var(--royal)"}}>
                <strong style={{color:"var(--royal)"}}>{activeGrade} TEKS:</strong> {w.teks_by_grade[activeGrade] || "Not defined"}
              </div>
              <div className="hidden print:block">
                {Object.entries(w.teks_by_grade).map(([g,t]) => (
                  <div key={g} className="mb-2 text-sm"><strong style={{color:"var(--royal)"}}>{g} TEKS:</strong> {t}</div>
                ))}
              </div>
            </div>
          )}
          
          {/* Games */}
          <div>
            <h3 className="font-display font-black text-xl uppercase mb-2" style={{color:"var(--royal)"}}>Games & Activities ({w.games?.length || 0})</h3>
            <div className="space-y-3">
              {(w.games || []).map((g,i) => (
                <details key={i} className="card p-0 overflow-hidden" style={{background:"var(--surface)"}} open={i===0}>
                  <summary className="p-3 cursor-pointer flex items-center gap-2 select-none" style={{background:"var(--bg)"}}>
                    <ChevronRight size={16} className="transition-transform" style={{transformOrigin:"center"}}/>
                    <span className="font-display font-black uppercase tracking-tight flex-1">
                      {g.emoji && <span className="mr-1">{g.emoji}</span>}{g.name}
                    </span>
                    <span className="chip chip-blue !text-[0.65rem] !py-0.5">{g.grade}</span>
                    <span className="chip !text-[0.65rem] !py-0.5">{g.time}</span>
                  </summary>
                  <div className="p-4 space-y-3 text-sm">
                    <div><strong className="label" style={{color:"var(--royal)"}}>Equipment:</strong> <span className="ml-1">{g.equipment}</span></div>
                    {g.description && <div><strong className="label" style={{color:"var(--royal)"}}>Description:</strong>
                      <p className="mt-1">{g.description}</p></div>}
                    {g.character_focus && (
                      <div className="card p-3" style={{background:"var(--bg)", borderLeft:"4px solid var(--royal)"}}>
                        <strong className="label" style={{color:"var(--royal)"}}>Character Focus:</strong>
                        <p className="mt-1">{g.character_focus}</p>
                      </div>
                    )}
                    {g.five_e_html && (
                      <details>
                        <summary className="cursor-pointer font-display font-bold uppercase text-xs" style={{color:"var(--royal)"}}>5E Lesson Plan</summary>
                        <div className="va-html mt-2 text-sm" dangerouslySetInnerHTML={{__html: cleanHtml(g.five_e_html)}}/>
                      </details>
                    )}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- SECTIONS INDEX ----------
function SectionsIndex() {
  const [sections, setSections] = useState([]);
  useEffect(() => { axios.get(`${API}/sections`).then(r=>setSections(r.data)); }, []);
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="font-display font-black text-5xl uppercase tracking-tighter italic mb-2">All <span style={{color:"var(--royal)"}}>Sections</span></h1>
      <p className="mb-6" style={{color:"var(--muted)"}}>Every chapter of the original Panthers PE curriculum — fully restored.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {sections.map(s => (
          <Link key={s.slug} to={`/sections/${s.slug}`} data-testid={`section-card-${s.slug}`} className="card p-5 block" style={{background:"var(--surface)"}}>
            <div className="text-4xl mb-2">{s.emoji}</div>
            <h2 className="font-display font-black text-xl uppercase leading-tight tracking-tight mb-2">{s.title}</h2>
            <span className="chip chip-blue">{s.card_count} entries</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ---------- SECTION PAGE ----------
function SectionPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => {
    // Redirect weekly-lessons section to dedicated /weeks UI
    if (slug && slug.startsWith("weekly-lessons")) { nav("/weeks", { replace: true }); return; }
    setData(null);
    axios.get(`${API}/sections/${slug}`).then(r=>setData(r.data)).catch(()=>setData({error: true}));
  }, [slug, nav]);
  if (!data) return <div className="text-center py-20"><Loader2 className="animate-spin inline" size={32}/></div>;
  if (data.error) return <div className="text-center py-20">Section not found.</div>;
  const filtered = q ? data.cards.filter(c => (c.name + " " + c.description).toLowerCase().includes(q.toLowerCase())) : data.cards;
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <Link to="/sections" className="btn-sec !text-xs !py-1.5 !px-3 inline-block mb-4 no-print">← All Sections</Link>
      <div className="flex items-start gap-3 mb-3">
        <div className="text-5xl">{data.emoji}</div>
        <h1 className="font-display font-black text-3xl sm:text-5xl uppercase tracking-tight leading-none">{data.title}</h1>
      </div>
      <div className="flex items-center gap-2 mb-6">
        <span className="chip chip-blue">{data.card_count} entries</span>
      </div>
      {data.intro_html && (
        <div className="card p-5 mb-6 va-html" style={{background:"var(--surface)"}} dangerouslySetInnerHTML={{__html: cleanHtml(data.intro_html)}}/>
      )}
      {data.cards.length > 8 && (
        <div className="relative mb-4">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2" style={{color:"var(--muted)"}}/>
          <input data-testid="section-search" value={q} onChange={e=>setQ(e.target.value)} placeholder={`Search ${data.card_count} entries...`} className="input pl-10"/>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {filtered.map(c => <ContentCard key={c.id} c={c}/>)}
      </div>
    </div>
  );
}

function ContentCard({ c }) {
  const { user, toggleFav } = useAuth();
  const isFav = user?.favorites?.includes(c.id);
  return (
    <Link to={`/content/${c.id}`} data-testid={`content-card-${c.id}`} className="card block p-0 overflow-hidden" style={{background:"var(--surface)"}}>
      <div className="p-4 text-white relative" style={{background: c.is_game ? "var(--royal)" : "#0A0A0A"}}>
        <div className="flex items-start justify-between mb-1">
          <span className="chip bg-white text-black border-white">{c.grade || c.category || "REF"}</span>
          {user && (
            <button onClick={e=>{e.preventDefault();e.stopPropagation();toggleFav(c.id);}} data-testid={`fav-${c.id}`} className="p-1">
              <Heart size={16} fill={isFav?"#fff":"none"} strokeWidth={2.5}/>
            </button>
          )}
        </div>
        <div className="flex items-start gap-2">
          {c.emoji && <span className="text-xl">{c.emoji}</span>}
          <h3 className="font-display font-black text-lg uppercase leading-tight tracking-tight">{c.name}</h3>
        </div>
        {c.is_game && c.engagement > 0 && <div className="flex gap-1 mt-1">{Array.from({length:c.engagement}).map((_,i)=><Zap key={i} size={10} fill="#FBBF24" strokeWidth={0}/>)}</div>}
      </div>
      <div className="p-3 text-sm">
        <p className="line-clamp-3" style={{color:"var(--muted)"}}>{c.description}</p>
      </div>
    </Link>
  );
}

// ---------- CONTENT PAGE (full HTML fidelity) ----------
function ContentPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const { user, toggleFav } = useAuth();
  useEffect(() => { axios.get(`${API}/cards/${id}`).then(r=>setC(r.data)).catch(()=>nav("/")); }, [id, nav]);
  if (!c) return <div className="text-center py-20"><Loader2 className="animate-spin inline" size={32}/></div>;
  const isFav = user?.favorites?.includes(c.id);
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="content-detail">
      <div className="no-print mb-4 flex flex-wrap gap-2 items-center justify-between">
        <Link to={`/sections/${c.section_slug}`} className="btn-sec !text-xs !py-2 !px-3">← Back to Section</Link>
        <div className="flex gap-2">
          {user && <button data-testid="fav-toggle" onClick={()=>toggleFav(c.id)} className="btn-sec !text-xs !py-2 !px-3"><Heart size={14} className="inline" fill={isFav?"currentColor":"none"}/> {isFav?"Saved":"Save"}</button>}
          <ShareBtn id={c.id}/>
          <button data-testid="print-btn" onClick={()=>window.print()} className="btn-pri !text-xs !py-2 !px-3"><Printer size={14} className="inline mr-1"/>Print / PDF</button>
          {c.is_game && <Link to={`/games/${c.id}`} className="btn-sec !text-xs !py-2 !px-3">Structured View</Link>}
        </div>
      </div>
      <div className="p-6 text-white mb-6" style={{background: c.is_game ? "var(--royal)" : "#0A0A0A", border:"2px solid var(--text)"}}>
        <div className="flex flex-wrap gap-2 mb-2">
          {c.grade && <span className="chip bg-white text-black border-white">{c.grade}</span>}
          {c.category && <span className="chip bg-white text-black border-white">{c.category}</span>}
          {c.time && <span className="chip bg-white text-black border-white">{c.time}</span>}
        </div>
        <div className="flex items-start gap-3">
          {c.emoji && <span className="text-4xl">{c.emoji}</span>}
          <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight">{c.name}</h1>
        </div>
      </div>
      {c.html_content ? (
        <div className="va-html card p-5" style={{background:"var(--surface)"}} dangerouslySetInnerHTML={{__html: cleanHtml(c.html_content)}}/>
      ) : (
        <div className="card p-5" style={{background:"var(--surface)"}}>{c.description}</div>
      )}
    </div>
  );
}

// ---------- WEATHER PAGE ----------
function WeatherPage() {
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="font-display font-black text-5xl uppercase tracking-tighter italic mb-2">Weather <span style={{color:"var(--royal)"}}>Hub</span></h1>
      <p className="mb-6" style={{color:"var(--muted)"}}>Live Van Alstyne, TX conditions + smart PE recommendations.</p>
      <WeatherWidget/>
      <div className="grid sm:grid-cols-2 gap-4 mt-6">
        {[
          ["PRIME OUTDOOR (60-80°F)","success","Ultimate Frisbee, Track, Field Day, Scavenger Hunts"],
          ["HEAT CAUTION (>92°F)","alert","Water breaks, shade, low-intensity, early start"],
          ["INDOOR (Rain/Storm)","alert","Parachute, Scooters, Floor Hockey, Freeze Dance"],
          ["BUNDLE UP (<45°F)","blue","High-energy warm-ups, layer-friendly games"],
        ].map(([t,c,d],i) => (
          <div key={i} className="card p-4" style={{background:"var(--surface)"}}>
            <span className={`chip chip-${c==="blue"?"blue":c}`}>{t}</span>
            <p className="mt-2 text-sm">{d}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- AI GAME GENERATOR ----------
function AIGen() {
  const { user } = useAuth();
  const [form, setForm] = useState({ grade: "K-4", equipment: "Cones, soft balls", theme: "Cooperation and listening", minutes: 12 });
  const [game, setGame] = useState(null); const [loading, setLoading] = useState(false); const [err, setErr] = useState("");
  const gen = async e => {
    e.preventDefault();
    if (!user) { setErr("Please login or sign up to use AI Coach."); return; }
    setLoading(true); setErr(""); setGame(null);
    try {
      const { data } = await axios.post(`${API}/ai/generate-game`, form);
      setGame(data);
    } catch (e) { setErr(fmtErr(e.response?.data?.detail) || "AI generation failed"); }
    finally { setLoading(false); }
  };
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center gap-2 mb-2"><Sparkles size={28} style={{color:"var(--royal)"}}/><h1 className="font-display font-black text-5xl uppercase tracking-tighter italic">AI <span style={{color:"var(--royal)"}}>Coach</span></h1></div>
      <p className="mb-6" style={{color:"var(--muted)"}}>Generate a custom TEKS-aligned PE game with full 5E lesson plan in seconds.</p>
      <form onSubmit={gen} className="card p-5 space-y-3" style={{background:"var(--surface)"}}>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className="label block mb-1">Grade Focus</label>
            <select data-testid="ai-grade" className="input" value={form.grade} onChange={e=>setForm({...form,grade:e.target.value})}>
              {["K","1st","2nd","3rd","4th","K-4","K-5"].map(g=><option key={g}>{g}</option>)}
            </select></div>
          <div><label className="label block mb-1">Minutes</label>
            <input data-testid="ai-minutes" type="number" min="5" max="45" className="input" value={form.minutes} onChange={e=>setForm({...form,minutes:+e.target.value})}/></div>
        </div>
        <div><label className="label block mb-1">Equipment Available</label>
          <input data-testid="ai-equipment" className="input" value={form.equipment} onChange={e=>setForm({...form,equipment:e.target.value})}/></div>
        <div><label className="label block mb-1">Theme / Skill Focus</label>
          <input data-testid="ai-theme" className="input" placeholder="e.g. cooperation, throwing accuracy, listening" value={form.theme} onChange={e=>setForm({...form,theme:e.target.value})}/></div>
        {err && <div className="flex gap-2 items-center text-sm p-2 border-2" style={{borderColor:"var(--alert)",color:"var(--alert)"}}><AlertCircle size={16}/>{err}</div>}
        <button data-testid="ai-submit" disabled={loading} className="btn-pri w-full flex items-center justify-center gap-2">
          {loading ? <><Loader2 className="animate-spin" size={16}/> Generating...</> : <><Sparkles size={16}/> Generate Game</>}
        </button>
      </form>

      {game && (
        <div className="mt-6" data-testid="ai-result">
          <div className="chip chip-success mb-3"><Sparkles size={12} className="inline mr-1"/>AI GENERATED</div>
          <div className="p-5 text-white mb-4" style={{background:"var(--royal)", border:"2px solid var(--text)"}}>
            <div className="flex flex-wrap gap-2 mb-2">
              <span className="chip bg-white text-black border-white">{game.grade}</span>
              <span className="chip bg-white text-black border-white">{game.category}</span>
              <span className="chip bg-white text-black border-white">{game.time}</span>
            </div>
            <h2 className="font-display font-black text-3xl uppercase">{game.name}</h2>
            <p className="mt-2">{game.description}</p>
          </div>
          <div className="card p-4 mb-3" style={{background:"var(--surface)"}}>
            <strong className="label" style={{color:"var(--royal)"}}>Equipment:</strong> <span className="ml-2">{game.equipment}</span>
          </div>
          <div className="card p-4 mb-3" style={{background:"var(--surface)"}}>
            <strong className="label" style={{color:"var(--royal)"}}>Character Focus:</strong> <p className="mt-1">{game.character_focus}</p>
          </div>
          <div className="card p-4 mb-3" style={{background:"var(--surface)"}}>
            <strong className="label block mb-2" style={{color:"var(--royal)"}}>TEKS</strong>
            <ul>{game.teks?.map((t,i)=><li key={i} className="text-sm">• {t}</li>)}</ul>
          </div>
          <div className="card p-4 mb-3" style={{background:"var(--surface)"}}>
            <strong className="label block mb-2" style={{color:"var(--royal)"}}>5E Plan</strong>
            {Object.entries(game.five_e||{}).map(([k,v])=>(<div key={k} className="py-1 text-sm"><span className="label" style={{color:"var(--royal)"}}>{k}: </span>{v}</div>))}
          </div>
          <div className="card p-4" style={{background:"var(--surface)"}}>
            <strong className="label block mb-2" style={{color:"var(--royal)"}}>Grade Modifications</strong>
            {Object.entries(game.modifications||{}).map(([k,v])=>(<div key={k} className="py-1 text-sm"><span className="chip chip-blue mr-2">{k}</span>{v}</div>))}
          </div>
        </div>
      )}
      <AIWeekly/>
    </div>
  );
}

// ---------- FAVORITES ----------
function Favorites() {
  const { user } = useAuth();
  const [games, setGames] = useState([]);
  const [videos, setVideos] = useState([]);
  useEffect(() => {
    if (!user) return;
    const favs = user.favorites || [];
    const videoIds = favs.filter(id => id.startsWith("video:")).map(id => id.slice(6));
    const gameIds = favs.filter(id => !id.startsWith("video:"));
    const allVids = [
      ...TOT_VIDEOS.map(v => ({...v, _tag:"This or That"})),
      ...POKO_VIDEOS.map(v => ({...v, _tag:"Pick One Kick One"})),
      ...LLM_VIDEOS.map(v => ({...v, _tag:"Locomotor L&M"})),
      ...DICE_VIDEOS.map(v => ({...v, _tag:"Dice Fitness"})),
    ];
    setVideos(allVids.filter(v => videoIds.includes(v.id)));
    Promise.all(gameIds.map(id => axios.get(`${API}/games/${id}`).then(r=>r.data).catch(()=>null)))
      .then(r => setGames(r.filter(Boolean)));
  }, [user]);
  if (!user) return (
    <div className="max-w-4xl mx-auto px-6 py-12 text-center">
      <Heart size={48} className="inline mb-3" strokeWidth={2.5} style={{color:"var(--royal)"}}/>
      <h1 className="font-display text-4xl uppercase font-black mb-2">Save Your Favorites</h1>
      <p style={{color:"var(--muted)"}}>Sign up or log in to save games + This or That videos + build custom lesson plans.</p>
    </div>
  );
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-display font-black text-5xl uppercase tracking-tighter italic">My <span style={{color:"var(--royal)"}}>Favorites</span></h1>
        <Link to="/planner" className="btn-pri !text-sm !py-2 !px-4"><CalendarDays size={14} className="inline mr-1"/>Build a Lesson</Link>
      </div>
      {games.length === 0 && videos.length === 0 ? (
        <div className="card p-8 text-center" style={{background:"var(--surface)"}}>
          <Heart size={40} className="inline mb-2" style={{color:"var(--muted)"}}/>
          <p style={{color:"var(--muted)"}}>No favorites yet. Tap the ♥ on any game or This or That video to save it here.</p>
          <div className="mt-4 flex gap-2 justify-center">
            <Link to="/games" className="btn-sec !text-xs !py-2 !px-3">Browse Games</Link>
            <Link to="/brain-breaks" className="btn-sec !text-xs !py-2 !px-3">Brain Breaks</Link>
          </div>
        </div>
      ) : (
        <>
          {games.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-3">
                <h2 className="font-display font-black text-2xl uppercase" style={{color:"var(--royal)"}}>Games</h2>
                <span className="chip chip-blue">{games.length}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">{games.map(g => <GameCard key={g.id} g={g}/>)}</div>
            </div>
          )}
          {videos.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <h2 className="font-display font-black text-2xl uppercase" style={{color:"var(--royal)"}}>Brain Breaks</h2>
                <span className="chip chip-blue">{videos.length}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">{videos.map(v => <VideoCard key={v.id} v={v} tag={v._tag}/>)}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------- SMART DAILY PICKER ----------
function SmartPicker() {
  const [grade, setGrade] = useState("K-4");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback((g) => {
    setLoading(true);
    axios.get(`${API}/picker/today`, { params: { grade: g }}).then(r=>setData(r.data)).finally(()=>setLoading(false));
  }, []);
  useEffect(() => { load(grade); }, [grade, load]);
  return (
    <section className="border-t-2 border-black dark:border-white" style={{background:"var(--bg)"}}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
          <div>
            <div className="chip chip-blue mb-2"><Sparkles size={12} className="inline mr-1"/>SMART PICKER</div>
            <h2 className="font-display font-black text-4xl uppercase tracking-tight flex items-center gap-2"><Wand2 size={28}/> Today&apos;s Top 3 Picks</h2>
            <p className="mt-1 text-sm" style={{color:"var(--muted)"}}>
              {data?.weather_status && <span className="font-bold">{data.weather_status} ({data.current_temp}°F)</span>}{data?.weather_tip && <> · {data.weather_tip}</>}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {["K","1st","2nd","3rd","4th","K-4"].map(g => (
              <button key={g} data-testid={`picker-grade-${g}`} onClick={()=>setGrade(g)} className={`chip cursor-pointer ${grade===g?"chip-blue":""}`}>{g}</button>
            ))}
            <button data-testid="picker-refresh" onClick={()=>load(grade)} className="chip cursor-pointer"><Shuffle size={11} className="inline mr-1"/>Refresh</button>
          </div>
        </div>
        {loading ? <div className="text-center py-8"><Loader2 className="animate-spin inline" size={28}/></div> :
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="picker-results">
            {(data?.picks || []).map((g,i) => (
              <div key={g.id} className="relative">
                <div className="absolute -top-3 -left-3 z-10 font-display font-black text-5xl px-3" style={{color:"var(--royal)", WebkitTextStroke:"2px var(--text)", background:"transparent"}}>{i+1}</div>
                <GameCard g={g}/>
              </div>
            ))}
          </div>
        }
      </div>
    </section>
  );
}

// ---------- CREATE CUSTOM GAME ----------
function CreateGame() {
  const { user } = useAuth();
  const nav = useNavigate();
  // Optional ?edit=<game_id> triggers edit mode. Preloads the game and PATCHes on save.
  const editId = new URLSearchParams(window.location.search).get("edit");
  const [form, setForm] = useState({
    name: "", grade: "K-4", category: "Movement Skills", equipment: "", time: "10-15 min",
    description: "", character_focus: "", teks: "", cross_curricular: "",
    youtube_url: "", engagement: 4,
    modK: "", mod1: "", mod2: "", mod3: "", mod4: "",
    fe_engage: "", fe_explore: "", fe_explain: "", fe_elaborate: "", fe_evaluate: "",
  });
  const [steps, setSteps] = useState([""]);  // ordered "How to Play" steps
  const [loading, setLoading] = useState(false); const [err, setErr] = useState("");
  const [loadingGame, setLoadingGame] = useState(!!editId);
  const [notOwner, setNotOwner] = useState(false);

  useEffect(() => {
    if (!editId || !user) return;
    axios.get(`${API}/games/${editId}`).then(r => {
      const g = r.data;
      if (g.created_by && g.created_by !== user.id) { setNotOwner(true); setLoadingGame(false); return; }
      setForm({
        name: g.name || "", grade: g.grade || "K-4", category: g.category || "Movement Skills",
        equipment: g.equipment || "", time: g.time || "10-15 min",
        description: g.description || "", character_focus: g.character_focus || "",
        teks: (g.teks || []).join("\n"),
        cross_curricular: g.cross_curricular || "", youtube_url: g.youtube_url || "",
        engagement: g.engagement || 4,
        modK: g.modifications?.K || "", mod1: g.modifications?.["1st"] || "",
        mod2: g.modifications?.["2nd"] || "", mod3: g.modifications?.["3rd"] || "",
        mod4: g.modifications?.["4th"] || "",
        fe_engage: g.five_e?.engage || "", fe_explore: g.five_e?.explore || "",
        fe_explain: g.five_e?.explain || "", fe_elaborate: g.five_e?.elaborate || "",
        fe_evaluate: g.five_e?.evaluate || "",
      });
      setSteps((g.how_to_play && g.how_to_play.length) ? g.how_to_play : [""]);
      setLoadingGame(false);
    }).catch(() => { setErr("Could not load the game to edit."); setLoadingGame(false); });
  }, [editId, user]);

  if (!user) return <div className="max-w-4xl mx-auto px-6 py-12 text-center"><h1 className="font-display text-3xl uppercase font-black">Login to create games</h1></div>;
  if (loadingGame) return <div className="text-center py-20"><Loader2 className="animate-spin inline" size={32}/></div>;
  if (notOwner) return (
    <div className="max-w-4xl mx-auto px-6 py-12 text-center">
      <h1 className="font-display text-3xl uppercase font-black mb-2">Not your game</h1>
      <p style={{color:"var(--muted)"}}>Only the original creator can edit this game.</p>
      <Link to={`/games/${editId}`} className="btn-sec inline-block mt-4 !text-xs !py-2 !px-3">← Back to game</Link>
    </div>
  );

  const submit = async e => {
    e.preventDefault(); setLoading(true); setErr("");
    const cleanSteps = steps.map(s => s.trim()).filter(Boolean);
    const payload = {
      name: form.name, grade: form.grade, category: form.category,
      equipment: form.equipment, time: form.time, description: form.description,
      character_focus: form.character_focus,
      teks: form.teks.split("\n").map(s=>s.trim()).filter(Boolean),
      cross_curricular: form.cross_curricular || null,
      youtube_url: form.youtube_url || null,
      engagement: +form.engagement,
      how_to_play: cleanSteps,
      modifications: Object.fromEntries(["K","1st","2nd","3rd","4th"].map((k,i)=>[k, form["mod"+(i===0?"K":i)] || ""]).filter(([_,v])=>v)),
      five_e: Object.fromEntries(["engage","explore","explain","elaborate","evaluate"].map(k=>[k, form["fe_"+k] || ""]).filter(([_,v])=>v)),
    };
    try {
      if (editId) {
        const { data } = await axios.patch(`${API}/games/${editId}`, payload);
        nav(`/games/${data.id || editId}`);
      } else {
        const { data } = await axios.post(`${API}/games`, payload);
        nav(`/games/${data.id}`);
      }
    } catch (e) { setErr(fmtErr(e.response?.data?.detail) || "Failed"); }
    finally { setLoading(false); }
  };

  const updateStep = (i, v) => setSteps(s => s.map((x, idx) => idx === i ? v : x));
  const addStep = () => setSteps(s => [...s, ""]);
  const removeStep = (i) => setSteps(s => s.length <= 1 ? [""] : s.filter((_, idx) => idx !== i));
  const moveStep = (i, dir) => setSteps(s => {
    const j = i + dir; if (j < 0 || j >= s.length) return s;
    const copy = [...s]; [copy[i], copy[j]] = [copy[j], copy[i]]; return copy;
  });

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="font-display font-black text-5xl uppercase tracking-tighter italic mb-2">
        {editId ? <>Edit <span style={{color:"var(--royal)"}}>Your Game</span></> : <>Create a <span style={{color:"var(--royal)"}}>Custom Game</span></>}
      </h1>
      <p className="mb-6" style={{color:"var(--muted)"}}>
        {editId ? "Update your play and save. Only you can edit this game." : "Add your own play to the Panthers playbook."}
      </p>
      <form onSubmit={submit} className="card p-5 space-y-4" style={{background:"var(--surface)"}}>
        <div><label className="label block mb-1">Game Name *</label><input data-testid="cg-name" required className="input" value={form.name} onChange={e=>setForm({...form,name:e.target.value})}/></div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div><label className="label block mb-1">Grade</label>
            <select data-testid="cg-grade" className="input" value={form.grade} onChange={e=>setForm({...form,grade:e.target.value})}>
              {["K","1st","2nd","3rd","4th","K-4","K-5"].map(g=><option key={g}>{g}</option>)}
            </select></div>
          <div><label className="label block mb-1">Category</label>
            <select data-testid="cg-category" className="input" value={form.category} onChange={e=>setForm({...form,category:e.target.value})}>
              {["Movement Skills","Cooperation","Listening Skills","Character Focus","Cross-Curricular","Adapted/Inclusive"].map(c=><option key={c}>{c}</option>)}
            </select></div>
          <div><label className="label block mb-1">Engagement (1-5)</label><input data-testid="cg-engagement" type="number" min="1" max="5" className="input" value={form.engagement} onChange={e=>setForm({...form,engagement:e.target.value})}/></div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className="label block mb-1">Equipment</label><input data-testid="cg-equipment" className="input" value={form.equipment} onChange={e=>setForm({...form,equipment:e.target.value})}/></div>
          <div><label className="label block mb-1">Time</label><input data-testid="cg-time" className="input" value={form.time} onChange={e=>setForm({...form,time:e.target.value})}/></div>
        </div>
        <div><label className="label block mb-1">Description *</label><textarea data-testid="cg-description" required rows={3} className="input" value={form.description} onChange={e=>setForm({...form,description:e.target.value})}/></div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="label">How to Play — Steps (in order)</label>
            <button type="button" data-testid="cg-add-step" onClick={addStep} className="btn-sec !text-xs !py-1 !px-2"><Plus size={12} className="inline"/> Add step</button>
          </div>
          <div className="space-y-2">
            {steps.map((s, i) => (
              <div key={i} className="flex gap-2 items-start" data-testid={`cg-step-row-${i}`}>
                <div className="chip chip-blue shrink-0 !w-8 justify-center flex" style={{minWidth:"2rem"}}>{i+1}</div>
                <textarea
                  data-testid={`cg-step-${i}`}
                  rows={2}
                  className="input flex-1"
                  placeholder={`Step ${i+1} — e.g., ${i===0?"Explain the boundaries and safety rules.":"Blow whistle and start play."}`}
                  value={s}
                  onChange={e=>updateStep(i, e.target.value)}
                />
                <div className="flex flex-col gap-1 shrink-0">
                  <button type="button" onClick={()=>moveStep(i,-1)} disabled={i===0} className="btn-sec !text-xs !py-1 !px-2 disabled:opacity-30" title="Move up">↑</button>
                  <button type="button" onClick={()=>moveStep(i,1)} disabled={i===steps.length-1} className="btn-sec !text-xs !py-1 !px-2 disabled:opacity-30" title="Move down">↓</button>
                  <button type="button" data-testid={`cg-step-remove-${i}`} onClick={()=>removeStep(i)} className="btn-sec !text-xs !py-1 !px-2" title="Remove step"><X size={12}/></button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div><label className="label block mb-1">Character Focus</label><textarea data-testid="cg-character" rows={2} className="input" value={form.character_focus} onChange={e=>setForm({...form,character_focus:e.target.value})}/></div>
        <div><label className="label block mb-1">TEKS (one per line)</label><textarea data-testid="cg-teks" rows={2} className="input" placeholder={"TEKS K.1A: Demonstrate...\nTEKS K.2C: Follow..."} value={form.teks} onChange={e=>setForm({...form,teks:e.target.value})}/></div>
        <div><label className="label block mb-1">Cross-Curricular (optional)</label><input data-testid="cg-cross" className="input" value={form.cross_curricular} onChange={e=>setForm({...form,cross_curricular:e.target.value})}/></div>
        <div><label className="label block mb-1">YouTube URL (optional)</label><input data-testid="cg-yt" className="input" value={form.youtube_url} onChange={e=>setForm({...form,youtube_url:e.target.value})}/></div>
        
        <details>
          <summary className="cursor-pointer font-display font-black uppercase text-sm" style={{color:"var(--royal)"}}>5E Lesson Plan (optional)</summary>
          <div className="mt-3 space-y-2">
            {[["engage","Engage – Hook"],["explore","Explore – Try"],["explain","Explain – Teach"],["elaborate","Elaborate – Play"],["evaluate","Evaluate – Check"]].map(([k,l]) => (
              <div key={k}><label className="label block mb-1">{l}</label><input data-testid={`cg-5e-${k}`} className="input" value={form["fe_"+k]} onChange={e=>setForm({...form,["fe_"+k]:e.target.value})}/></div>
            ))}
          </div>
        </details>
        
        <details>
          <summary className="cursor-pointer font-display font-black uppercase text-sm" style={{color:"var(--royal)"}}>Grade Modifications (optional)</summary>
          <div className="mt-3 space-y-2">
            {[["K","K"],["1","1st"],["2","2nd"],["3","3rd"],["4","4th"]].map(([k,l]) => (
              <div key={k}><label className="label block mb-1">{l}</label><input data-testid={`cg-mod-${l}`} className="input" value={form["mod"+k]} onChange={e=>setForm({...form,["mod"+k]:e.target.value})}/></div>
            ))}
          </div>
        </details>
        
        {err && <div className="flex gap-2 items-center text-sm p-2 border-2" style={{borderColor:"var(--alert)",color:"var(--alert)"}}><AlertCircle size={16}/>{err}</div>}
        <button data-testid="cg-submit" disabled={loading} className="btn-pri w-full flex items-center justify-center gap-2">
          {loading ? <><Loader2 className="animate-spin" size={16}/> Saving...</> :
           editId ? <><Check size={16}/> Save Changes</> : <><Plus size={16}/> Add to Playbook</>}
        </button>
      </form>
    </div>
  );
}

// ---------- LESSON PLANNER ----------
const DAYS = [["mon","Monday"],["tue","Tuesday"],["wed","Wednesday"],["thu","Thursday"],["fri","Friday"]];

function Planner() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [plans, setPlans] = useState([]);
  const [activePlan, setActivePlan] = useState(null);
  const [picker, setPicker] = useState(null);
  const [allGames, setAllGames] = useState([]);
  const [q, setQ] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState("");
  // Read ?plan=<id> from URL to deep-link from Coach [Name]'s Weeks section
  const urlPlanId = useMemo(() => {
    if (typeof window === "undefined") return null;
    return new URLSearchParams(window.location.search).get("plan");
  }, []);
  const loadPlans = useCallback(async () => {
    if (!user) return;
    const { data } = await axios.get(`${API}/plans`);
    setPlans(data);
    // Priority: URL param → currently selected → first plan
    if (data.length && !activePlan) {
      const wanted = urlPlanId && data.find(p => p.id === urlPlanId);
      setActivePlan(wanted ? wanted.id : data[0].id);
    }
    if (activePlan && !data.find(p => p.id === activePlan)) setActivePlan(data[0]?.id || null);
  }, [user, activePlan, urlPlanId]);
  const reloadLessons = useCallback(() => {
    if (!user || !activePlan) return;
    axios.get(`${API}/lessons`, { params: { plan_id: activePlan }}).then(r=>setLessons(r.data));
  }, [user, activePlan]);
  useEffect(() => { loadPlans(); }, [loadPlans]);
  useEffect(() => { reloadLessons(); }, [reloadLessons]);
  useEffect(() => { if (picker) axios.get(`${API}/games?limit=500`).then(r=>setAllGames(r.data)); }, [picker]);
  const byDay = useMemo(() => {
    const m = {};
    for (const [k] of DAYS) m[k] = [];
    for (const l of lessons) { if (m[l.day]) m[l.day].push(l); }
    for (const k in m) m[k].sort((a,b)=>a.slot-b.slot);
    return m;
  }, [lessons]);
  const favSet = useMemo(() => new Set((user?.favorites || []).filter(id => !id.startsWith("video:"))), [user]);
  const favGames = useMemo(() => allGames.filter(g => favSet.has(g.id) && (!q || g.name.toLowerCase().includes(q.toLowerCase()) || (g.category||"").toLowerCase().includes(q.toLowerCase()))), [allGames, favSet, q]);
  const otherGames = useMemo(() => allGames.filter(g => !favSet.has(g.id) && (!q || g.name.toLowerCase().includes(q.toLowerCase()) || (g.category||"").toLowerCase().includes(q.toLowerCase()))).slice(0, q ? 200 : 60), [allGames, favSet, q]);
  const favVideos = useMemo(() => {
    const vids = (user?.favorites || []).filter(id => id.startsWith("video:")).map(id => id.slice(6));
    const all = [
      ...TOT_VIDEOS.map(v => ({...v, _tag:"This or That"})),
      ...POKO_VIDEOS.map(v => ({...v, _tag:"Pick One Kick One"})),
      ...LLM_VIDEOS.map(v => ({...v, _tag:"Locomotor L&M"})),
      ...DICE_VIDEOS.map(v => ({...v, _tag:"Dice Fitness"})),
    ];
    return all.filter(v => vids.includes(v.id) && (!q || v.title.toLowerCase().includes(q.toLowerCase())));
  }, [user, q]);
  if (!user) return <div className="max-w-4xl mx-auto px-6 py-12 text-center"><h1 className="font-display text-3xl uppercase font-black">Login to use the planner</h1></div>;

  const currentPlan = plans.find(p => p.id === activePlan);
  const addCard = async (cardId, meta) => {
    const body = { day: picker.day, slot: picker.slot, card_id: cardId, plan_id: activePlan };
    if (meta) body.item_meta = meta;
    await axios.post(`${API}/lessons`, body);
    setPicker(null); reloadLessons(); loadPlans();
  };
  const removeLesson = async (id) => { await axios.delete(`${API}/lessons/${id}`); reloadLessons(); loadPlans(); };
  const clearWeek = async () => {
    if (!window.confirm(`Clear ALL lessons from "${currentPlan?.name}"?`)) return;
    await axios.delete(`${API}/lessons`, { params: { plan_id: activePlan }});
    reloadLessons(); loadPlans();
  };
  const addWeek = async () => {
    const name = prompt("Name this week (e.g. \"Week of Aug 18\", \"Field Day Week\"):", "Week " + (plans.length + 1));
    if (!name) return;
    const { data } = await axios.post(`${API}/plans`, { name });
    await loadPlans();
    setActivePlan(data.id);
  };
  const savePlanName = async () => {
    if (!newName.trim() || newName === currentPlan?.name) { setRenaming(false); return; }
    await axios.patch(`${API}/plans/${activePlan}`, { name: newName });
    setRenaming(false); loadPlans();
  };
  const deleteWeek = async () => {
    if (plans.length <= 1) { alert("You need at least one week. Create another before deleting this one."); return; }
    if (!window.confirm(`DELETE the week "${currentPlan?.name}" and all its lessons?`)) return;
    await axios.delete(`${API}/plans/${activePlan}`);
    setActivePlan(null);
    loadPlans();
  };
  const togglePublish = async () => {
    if (!activePlan) return;
    await axios.patch(`${API}/plans/${activePlan}/publish`);
    loadPlans();
  };

  const printFullWeek = async () => {
    // Fetch full card data for every game in the plan
    const gameIds = [...new Set(lessons.filter(l => !l.card_id.startsWith("video:")).map(l => l.card_id))];
    const cards = await Promise.all(gameIds.map(id => axios.get(`${API}/cards/${id}`).then(r=>r.data).catch(()=>null)));
    const cardMap = Object.fromEntries(cards.filter(Boolean).map(c => [c.id, c]));
    const dayLabel = d => DAYS.find(x => x[0] === d)[1];
    const daySections = DAYS.map(([k, label]) => {
      const items = lessons.filter(l => l.day === k).sort((a,b)=>a.slot-b.slot);
      if (items.length === 0) return `<section class="day"><h2>${label}</h2><p class="empty">— no lessons —</p></section>`;
      return `<section class="day"><h2>${label}</h2>${items.map(l => {
        if (l.card_id.startsWith("video:")) {
          const m = l.item_meta || {};
          return `<div class="lesson video">
            <div class="lesson-head"><h3>${m.emoji||"🎬"} ${m.title||l.card_id}</h3>
              <div class="meta"><span>${m.tag||"Brain Break"}</span><span>${m.grade||""}</span><span>${m.duration||""}</span></div></div>
            <p><strong>YouTube:</strong> https://www.youtube.com/watch?v=${l.card_id.slice(6)}</p>
          </div>`;
        }
        const c = cardMap[l.card_id];
        if (!c) return `<div class="lesson"><h3>${l.card_id}</h3><p>(details unavailable)</p></div>`;
        return `<div class="lesson">
          <div class="lesson-head"><h3>${c.emoji||""} ${c.name}</h3>
            <div class="meta"><span>${c.grade}</span><span>${c.category}</span><span>${c.time}</span></div></div>
          <p><strong>Equipment:</strong> ${c.equipment}</p>
          <p><strong>Description:</strong> ${c.description||""}</p>
          ${c.character_focus?`<p><strong>Character Focus:</strong> ${c.character_focus}</p>`:""}
          ${(c.teks||[]).length?`<h4>TEKS</h4><ul>${c.teks.map(t=>"<li>"+t+"</li>").join("")}</ul>`:""}
          ${Object.keys(c.five_e||{}).filter(k=>c.five_e[k]).length?`<h4>5E Lesson</h4>${Object.entries(c.five_e).filter(([_,v])=>v).map(([k,v])=>`<p><strong>${k.toUpperCase()}:</strong> ${v}</p>`).join("")}`:""}
          ${Object.keys(c.modifications||{}).length?`<h4>Grade Modifications</h4><table>${Object.entries(c.modifications).map(([k,v])=>`<tr><th>${k}</th><td>${v}</td></tr>`).join("")}</table>`:""}
        </div>`;
      }).join("")}</section>`;
    }).join("");
    const html = `<html><head><title>${currentPlan?.name || "Weekly Plan"} — Panthers PE</title>
      <style>
        body{font-family:'DM Sans',sans-serif;color:#0A0A0A;background:white;padding:20px;line-height:1.5;}
        h1{font-family:'Barlow Condensed',sans-serif;font-weight:900;text-transform:uppercase;font-style:italic;color:#1E40AF;font-size:2.6rem;border-bottom:4px solid #1E40AF;padding-bottom:6px;margin:0 0 6px;}
        .sub{color:#525252;margin-bottom:20px;font-size:0.9rem;}
        h2{font-family:'Barlow Condensed',sans-serif;font-weight:900;text-transform:uppercase;color:#fff;background:#1E40AF;padding:8px 14px;margin:22px 0 12px;}
        h3{font-family:'Barlow Condensed',sans-serif;font-weight:900;text-transform:uppercase;margin:0;font-size:1.3rem;}
        h4{font-family:'Barlow Condensed',sans-serif;text-transform:uppercase;color:#1E40AF;margin:10px 0 4px;}
        .day{page-break-inside:auto;}
        .lesson{border:2px solid #0A0A0A;padding:14px;margin-bottom:12px;break-inside:avoid;}
        .lesson.video{border-color:#1E40AF;background:#F1F5F9;}
        .lesson-head{margin-bottom:8px;}
        .meta{display:flex;gap:6px;flex-wrap:wrap;margin-top:4px;font-size:0.72rem;font-weight:700;text-transform:uppercase;}
        .meta span{background:#0A0A0A;color:#fff;padding:2px 8px;}
        .empty{color:#a3a3a3;font-style:italic;margin:0 0 12px;}
        table{border-collapse:collapse;width:100%;font-size:0.85rem;}
        td,th{border:1px solid #ccc;padding:4px 8px;text-align:left;}
        th{background:#1E40AF;color:#fff;width:60px;}
        ul{margin:4px 0 8px 20px;}
        @page{margin:1.2cm;}
      </style></head><body>
      <h1>${currentPlan?.name || "Weekly Plan"}</h1>
      <div class="sub">Van Alstyne Panthers PE · ${lessons.length} lessons · Printed ${new Date().toLocaleDateString()}</div>
      ${daySections}
      </body></html>`;
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, "weekfull");
    if (win) {
      win.addEventListener("load", () => { win.print(); }, { once: true });
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <h1 className="font-display font-black text-5xl uppercase tracking-tighter italic flex items-center gap-2"><CalendarDays size={36}/> Weekly <span style={{color:"var(--royal)"}}>Planner</span></h1>
        <div className="flex gap-2 no-print flex-wrap">
          <button data-testid="planner-print" onClick={printFullWeek} className="btn-pri !text-xs !py-2 !px-3"><Printer size={14} className="inline mr-1"/>Print Full Week</button>
          <button data-testid="planner-clear" onClick={clearWeek} className="btn-sec !text-xs !py-2 !px-3"><Trash2 size={14} className="inline mr-1"/>Clear Lessons</button>
        </div>
      </div>

      {/* Week selector */}
      <div className="card p-4 mb-4 no-print" style={{background:"var(--surface)"}} data-testid="planner-weeks-bar">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <div className="label" style={{color:"var(--royal)"}}>My Weeks</div>
          <span className="chip chip-blue !text-[0.6rem] !py-0 !px-1.5">{plans.length}</span>
          <div className="ml-auto flex gap-2">
            <button data-testid="planner-add-week" onClick={addWeek} className="btn-pri !text-xs !py-2 !px-3"><Plus size={14} className="inline mr-1"/>Add New Week</button>
            {plans.length > 1 && <button data-testid="planner-delete-week" onClick={deleteWeek} className="btn-sec !text-xs !py-2 !px-3" title="Delete this week"><Trash2 size={14}/></button>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {plans.map(p => (
            <button key={p.id} data-testid={`planner-week-${p.id}`} onClick={()=>{setActivePlan(p.id); setRenaming(false);}}
              className={`chip cursor-pointer ${activePlan===p.id?"chip-blue":""}`}>
              {p.name} <span className="opacity-70">· {p.lesson_count}</span>
            </button>
          ))}
        </div>
        {currentPlan && (
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {renaming ? (
              <>
                <input autoFocus className="input !py-1.5 !text-sm max-w-xs" value={newName} onChange={e=>setNewName(e.target.value)}
                  onKeyDown={e=>{ if (e.key === "Enter") savePlanName(); if (e.key === "Escape") setRenaming(false); }}/>
                <button data-testid="planner-save-name" onClick={savePlanName} className="btn-pri !text-xs !py-1.5 !px-3"><Check size={14}/></button>
                <button onClick={()=>setRenaming(false)} className="btn-sec !text-xs !py-1.5 !px-3"><X size={14}/></button>
              </>
            ) : (
              <>
                <span className="font-display font-black uppercase text-lg">{currentPlan.name}</span>
                <button data-testid="planner-rename" onClick={()=>{setNewName(currentPlan.name); setRenaming(true);}} className="text-xs underline" style={{color:"var(--muted)"}}>rename</button>
                <button
                  data-testid="planner-toggle-publish"
                  onClick={togglePublish}
                  title={currentPlan.is_public ? "Currently published to public Weeks page. Click to unpublish." : "Draft — click to publish to the public Weeks page."}
                  className={`chip cursor-pointer !text-[0.65rem] !py-1 !px-2 ${currentPlan.is_public ? "chip-blue" : ""}`}
                >
                  {currentPlan.is_public ? "PUBLISHED · click to make draft" : "DRAFT · click to publish"}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      <p className="mb-6" style={{color:"var(--muted)"}}>Add favorites first for quick planning. Auto-saves per week. Print the whole week as a single packet.</p>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        {DAYS.map(([k,label]) => (
          <div key={k} className="card p-3 min-h-[280px]" style={{background:"var(--surface)"}} data-testid={`planner-day-${k}`}>
            <div className="font-display font-black text-lg uppercase mb-2 border-b-2 pb-1" style={{borderColor:"var(--royal)",color:"var(--royal)"}}>{label}</div>
            <div className="space-y-2">
              {byDay[k].map((l) => {
                const isVid = l.card_id.startsWith("video:");
                const meta = l.item_meta || {};
                const vid = isVid ? l.card_id.slice(6) : null;
                return (
                <div key={l.id} className="border-2 border-black dark:border-white p-2 text-sm group relative" style={isVid?{background:"var(--bg)"}:{}} data-testid={`planner-item-${l.id}`}>
                  <div className="font-bold text-xs uppercase font-display flex items-center gap-1" style={{color:"var(--royal)"}}>
                    {isVid ? <Video size={11}/> : null}
                    {isVid ? `${meta.tag || "Video"} · ${meta.duration || ""}` : `${l.card?.grade || ""} · ${l.card?.time || ""}`}
                  </div>
                  {isVid ? (
                    <a href={`https://www.youtube.com/watch?v=${vid}`} target="_blank" rel="noopener noreferrer" className="font-bold hover:underline">
                      {meta.emoji || "🎬"} {meta.title || l.card_id}
                    </a>
                  ) : (
                    <Link to={`/content/${l.card_id}`} className="font-bold hover:underline">
                      {l.card?.emoji} {l.card?.name || l.card_id}
                    </Link>
                  )}
                  <button onClick={()=>removeLesson(l.id)} data-testid={`planner-remove-${l.id}`} className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 no-print">
                    <X size={14}/>
                  </button>
                </div>);
              })}
              <button data-testid={`planner-add-${k}`} onClick={()=>setPicker({day:k, slot:byDay[k].length})} className="w-full border-2 border-dashed py-3 text-xs font-display uppercase font-bold no-print hover:border-royal hover:text-white hover:bg-royal" style={{borderColor:"var(--border)",color:"var(--muted)"}}>
                <Plus size={14} className="inline"/> Add
              </button>
            </div>
          </div>
        ))}
      </div>

      {picker && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={()=>setPicker(null)}>
          <div className="card p-5 max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e=>e.stopPropagation()} style={{background:"var(--surface)"}}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display font-black text-2xl uppercase">Add to {DAYS.find(d=>d[0]===picker.day)[1]}</h2>
              <button onClick={()=>setPicker(null)}><X size={22}/></button>
            </div>
            <div className="relative mb-3">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2" style={{color:"var(--muted)"}}/>
              <input autoFocus data-testid="planner-picker-search" className="input pl-10" placeholder="Search games..." value={q} onChange={e=>setQ(e.target.value)}/>
            </div>
            <div className="overflow-y-auto flex-1 space-y-4">
              <div>
                <div className="flex items-center gap-2 mb-2 sticky top-0 py-1" style={{background:"var(--surface)"}}>
                  <Heart size={14} fill="currentColor" style={{color:"var(--royal)"}}/>
                  <div className="label" style={{color:"var(--royal)"}}>My Favorites</div>
                  <span className="chip chip-blue !text-[0.6rem] !py-0 !px-1.5">{favGames.length + favVideos.length}</span>
                </div>
                {favGames.length === 0 && favVideos.length === 0 ? (
                  <div className="text-xs p-3 border-2 border-dashed" style={{borderColor:"var(--border)", color:"var(--muted)"}}>
                    No favorites yet. Tap the ♥ on any game or Brain Break to save it here for quick lesson-planning.
                  </div>
                ) : (
                  <div className="space-y-1">
                    {favGames.map(g => (
                      <button key={g.id} data-testid={`planner-pick-fav-${g.id}`} onClick={()=>addCard(g.id)} className="w-full text-left p-2 border-2 hover:border-black dark:hover:border-white flex justify-between items-center" style={{background:"var(--bg)", borderColor:"var(--royal)"}}>
                        <span><Heart size={12} fill="currentColor" className="inline mr-1" style={{color:"var(--royal)"}}/><span className="font-bold">{g.emoji} {g.name}</span> <span className="text-xs" style={{color:"var(--muted)"}}>· {g.grade} · {g.category}</span></span>
                        <Plus size={16}/>
                      </button>
                    ))}
                    {favVideos.map(v => (
                      <button key={v.id} data-testid={`planner-pick-video-${v.id}`}
                        onClick={()=>addCard(`video:${v.id}`, {title:v.title, emoji:v.emoji, grade:v.grade, duration:v.duration, tag:v._tag})}
                        className="w-full text-left p-2 border-2 hover:border-black dark:hover:border-white flex justify-between items-center"
                        style={{background:"var(--bg)", borderColor:"var(--royal)"}}>
                        <span><Video size={12} className="inline mr-1" style={{color:"var(--royal)"}}/><span className="font-bold">{v.emoji} {v.title}</span> <span className="text-xs" style={{color:"var(--muted)"}}>· {v._tag} · {v.duration}</span></span>
                        <Plus size={16}/>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2 sticky top-0 py-1" style={{background:"var(--surface)"}}>
                  <LayoutGrid size={14} style={{color:"var(--muted)"}}/>
                  <div className="label" style={{color:"var(--muted)"}}>All Games{q ? "" : " (first 60)"}</div>
                </div>
                <div className="space-y-1">
                  {otherGames.map(g => (
                    <button key={g.id} data-testid={`planner-pick-${g.id}`} onClick={()=>addCard(g.id)} className="w-full text-left p-2 border-2 border-transparent hover:border-black dark:hover:border-white flex justify-between items-center" style={{background:"var(--bg)"}}>
                      <span><span className="font-bold">{g.emoji} {g.name}</span> <span className="text-xs" style={{color:"var(--muted)"}}>· {g.grade} · {g.category}</span></span>
                      <Plus size={16}/>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- AI WEEKLY PLAN ----------
function AIWeekly() {
  const { user } = useAuth();
  const [form, setForm] = useState({ grade: "K-4", equipment: "Cones, soft balls, hoops", focus: "Cooperation + Throwing", week_label: "" });
  const [plan, setPlan] = useState(null); const [loading, setLoading] = useState(false); const [err, setErr] = useState("");
  const gen = async e => {
    e.preventDefault(); if (!user) { setErr("Login required"); return; }
    setLoading(true); setErr(""); setPlan(null);
    try { const { data } = await axios.post(`${API}/ai/weekly-plan`, form); setPlan(data); }
    catch (e) { setErr(fmtErr(e.response?.data?.detail) || "Failed"); }
    finally { setLoading(false); }
  };
  return (
    <div className="card p-5 mt-6" style={{background:"var(--surface)"}}>
      <h2 className="font-display font-black text-2xl uppercase mb-1 flex items-center gap-2"><CalendarDays size={22}/> AI Weekly Planner</h2>
      <p className="text-sm mb-3" style={{color:"var(--muted)"}}>Generate a full Mon-Fri PE plan with warm-ups, main games, cooldowns, TEKS, and character focus.</p>
      <form onSubmit={gen} className="space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <select data-testid="weekly-grade" className="input" value={form.grade} onChange={e=>setForm({...form,grade:e.target.value})}>{["K","1st","2nd","3rd","4th","K-4"].map(g=><option key={g}>{g}</option>)}</select>
          <input data-testid="weekly-week" className="input" placeholder="Week label (e.g. 'Week of Sep 8')" value={form.week_label} onChange={e=>setForm({...form,week_label:e.target.value})}/>
        </div>
        <input data-testid="weekly-equipment" className="input" placeholder="Equipment available" value={form.equipment} onChange={e=>setForm({...form,equipment:e.target.value})}/>
        <input data-testid="weekly-focus" className="input" placeholder="Focus theme (e.g. throwing accuracy)" value={form.focus} onChange={e=>setForm({...form,focus:e.target.value})}/>
        {err && <div className="flex gap-2 items-center text-sm p-2 border-2" style={{borderColor:"var(--alert)",color:"var(--alert)"}}><AlertCircle size={16}/>{err}</div>}
        <button data-testid="weekly-submit" disabled={loading} className="btn-pri w-full flex items-center justify-center gap-2">
          {loading ? <><Loader2 className="animate-spin" size={16}/> Building plan...</> : <><Wand2 size={16}/> Generate Week</>}
        </button>
      </form>
      {plan && (
        <div className="mt-5" data-testid="weekly-result">
          <div className="chip chip-success mb-2"><Sparkles size={12} className="inline mr-1"/>AI WEEK PLAN</div>
          <h3 className="font-display font-black text-2xl uppercase">{plan.week_label}</h3>
          <p className="italic mb-3">{plan.theme}</p>
          <div className="grid sm:grid-cols-5 gap-2">
            {(plan.days||[]).map((d,i) => (
              <div key={i} className="border-2 border-black dark:border-white p-2 text-xs">
                <div className="font-display font-black uppercase" style={{color:"var(--royal)"}}>{d.day}</div>
                <div className="font-bold mt-1">{d.focus}</div>
                <div className="mt-1"><strong>Warmup:</strong> {d.warmup}</div>
                <div className="mt-1"><strong>Game:</strong> {d.main_game}</div>
                <div className="mt-1"><strong>Cooldown:</strong> {d.cooldown}</div>
                <div className="mt-1 text-[0.65rem]" style={{color:"var(--muted)"}}>{d.teks}</div>
              </div>
            ))}
          </div>
          <div className="card p-3 mt-3" style={{background:"var(--bg)"}}>
            <strong className="label" style={{color:"var(--royal)"}}>Character Focus:</strong> {plan.character_focus}
          </div>
          <div className="card p-3 mt-2" style={{background:"var(--bg)"}}>
            <strong className="label" style={{color:"var(--royal)"}}>Assessment:</strong> {plan.assessment}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- BRAIN BREAKS ----------
const TOT_VIDEOS = [
  { id: "2D9h_aiJfkg", title: "Pet Edition", emoji: "🐾", theme: "Animals", grade: "K-4", duration: "5 min",
    description: "Cats or dogs? Birds or fish? Students pick sides then move like their choice.",
    note: "Cats vs dogs, birds vs fish — quick choices spark whole-class movement." },
  { id: "9wbNhvH3pxM", title: "100th Day of School", emoji: "💯", theme: "School", grade: "K-4", duration: "4 min",
    description: "100th-day-themed brain break with counting and movement.",
    note: "Perfect for 100th-day celebrations — quick picks + big-number movement." },
  { id: "Tg3CShvPQT4", title: "Animal Edition", emoji: "🦁", theme: "Animals", grade: "K-4", duration: "4 min",
    description: "Jungle vs safari picks. Great warm-up for zoo/animal units.",
    note: "Jungle vs safari picks. Great warm-up for zoo/animal units." },
  { id: "SF-Qj5GdN50", title: "Animals Workout", emoji: "🐎", theme: "Animals", grade: "K-4", duration: "5 min",
    description: "Pick your animal, then do the movement it inspires.",
    note: "Pick your animal, then do the movement it inspires." },
  { id: "BrVKFVfXN_E", title: "Sports Edition", emoji: "⚾", theme: "Sports", grade: "K-4", duration: "4 min",
    description: "Baseball or basketball? Great intro to sports units and Field Day prep.",
    note: "Baseball or basketball? Great intro to sports units and Field Day prep." },
  { id: "QDdnPD6M7l4", title: "Earth Day", emoji: "🌍", theme: "Weather", grade: "K-4", duration: "4 min",
    description: "Recycling, planting, and outdoor movement choices tied to Earth Day.",
    note: "Earth-Day themed picks with responsibility + character focus." },
  { id: "4GCZpy270WY", title: "Would You Rather · Sports", emoji: "🏈", theme: "Sports", grade: "1-4", duration: "5 min",
    description: "Choose your sport then do the workout.",
    note: "Blends brain-break with fitness choice — perfect Panthers energy." },
  { id: "_wtNe5wXd9k", title: "Superhero Edition", emoji: "🦸", theme: "Super Heroes", grade: "K-4", duration: "4 min",
    description: "Marvel vs DC, flight vs super strength — hero-mindset movement.",
    note: "Marvel vs DC, flight vs strength — hero mindset warm-up." },
  { id: "wGYJzp7Iiyc", title: "Disney Edition", emoji: "🏰", theme: "Disney", grade: "K-4", duration: "5 min",
    description: "Disney character-themed choices with movement responses.",
    note: "Character-focus brain break tied to favorite Disney movies." },
  { id: "Ew1gLmhTc90", title: "Winter Edition", emoji: "❄️", theme: "Winter", grade: "K-4", duration: "4 min",
    description: "Cold-day indoor rescue with winter-themed picks.",
    note: "Cold-day indoor rescue — quick choices + movement bursts." },
  { id: "Lz8VXTltTIk", title: "Weather Edition", emoji: "🌦️", theme: "Weather", grade: "K-4", duration: "4 min",
    description: "Sun vs rain, snow vs wind — pick + move.",
    note: "Ties into the Panthers Weather Hub — sun vs rain movement responses." },
];
const POKO_VIDEOS = [
  { id: "eA1BvwmeZEk", title: "Fall / Thanksgiving", emoji: "🍂", theme: "Fall", grade: "K-4", duration: "5 min",
    description: "Pick one fall item, kick one out — great November brain break.",
    note: "Fall-themed round: pick your favorite, kick the least favorite, all while moving." },
  { id: "N8Pw8pd5oKw", title: "100th Day of School", emoji: "💯", theme: "School", grade: "K-4", duration: "5 min",
    description: "100th-day themed round for milestone celebrations.",
    note: "Milestone-day movement + decision-making practice." },
  { id: "tCiBjNCbp74", title: "Movie Edition", emoji: "🎬", theme: "Movies", grade: "1-4", duration: "6 min",
    description: "Popular movies to keep or kick out.",
    note: "Kids love the pop-culture picks. Great transition tool." },
  { id: "99ITsTElb_4", title: "Marvel Superhero", emoji: "🦸", theme: "Super Heroes", grade: "K-4", duration: "5 min",
    description: "Pick a Marvel hero, kick one, hero-power movement between rounds.",
    note: "Assemble the class. Hero-focus + movement bursts." },
  { id: "UBSiEwzRdHU", title: "Sweets and Snacks", emoji: "🍬", theme: "Food", grade: "K-4", duration: "5 min",
    description: "Favorite treat rounds — great for nutrition tie-ins.",
    note: "Character-focus tie-in: choices + healthy-eating discussion." },
  { id: "DmNmSK4XJD4", title: "Dynamic Duo", emoji: "👯", theme: "Super Heroes", grade: "K-4", duration: "5 min",
    description: "Pick your dynamic duo — hero + sidekick combos.",
    note: "Teamwork character focus + fast decision practice." },
  { id: "hdrKCeIBSio", title: "Halloween", emoji: "🎃", theme: "Holidays", grade: "K-4", duration: "5 min",
    description: "Halloween-themed picks — spooky-fun October brain break.",
    note: "Great October transition — movement between rounds." },
];
const LLM_VIDEOS = [
  { id: "1hqUuLriFWU", title: "Original Locomotor Listen & Move", emoji: "🎧", theme: "Original", grade: "PreK-2", duration: "5 min",
    description: "The classic locomotor movement + listening skills warm-up.",
    note: "Students listen for the cue and respond with the matching locomotor movement — builds listening + gross motor skills." },
  { id: "rsizKZUzLzQ", title: "Locomotor Listen & Move #2", emoji: "🎧", theme: "Original", grade: "PreK-2", duration: "5 min",
    description: "Follow-up with new movement cues and pacing.",
    note: "Fresh cues, faster tempo — great for classes ready for the next level." },
  { id: "PkAIq9G5OIw", title: "Spring Edition", emoji: "🌷", theme: "Spring", grade: "PreK-2", duration: "5 min",
    description: "Spring-themed locomotor movements: hop like a bunny, flutter like a butterfly.",
    note: "Springtime cues (bunnies, butterflies, flowers) tied to core locomotor skills." },
  { id: "Dhi5M1vHybg", title: "Animal Edition", emoji: "🦓", theme: "Animals", grade: "PreK-2", duration: "5 min",
    description: "Move like the animals you hear called out.",
    note: "Character focus: patience + attention. Wait for your animal, then move." },
  { id: "VaUcuhTGQiA", title: "Barnyard Edition", emoji: "🐄", theme: "Animals", grade: "PreK-2", duration: "5 min",
    description: "Farm-animal locomotor moves — great for youngest learners.",
    note: "Great for PreK-K units on the farm. Sounds + movement pairing." },
  { id: "UKlVZQddlJ4", title: "Holiday Edition", emoji: "🎄", theme: "Holidays", grade: "PreK-2", duration: "8 min",
    description: "Holiday-themed cues with locomotor responses.",
    note: "December favorite — reindeer gallops, elf skips, snowman freezes." },
  { id: "QOZrIIQ4M0E", title: "Winter Edition", emoji: "❄️", theme: "Winter", grade: "PreK-2", duration: "8 min",
    description: "Winter cues: ice-skate slides, penguin waddles, snowball throws.",
    note: "Perfect indoor rescue on a cold day — big listening focus." },
  { id: "0RHe-Z7tFpo", title: "Halloween Edition", emoji: "🎃", theme: "Holidays", grade: "PreK-2", duration: "10 min",
    description: "Halloween cues with 10 different locomotor movements — no equipment needed.",
    note: "October classic — ghost floats, witch flies, monster stomps. Listening + gross motor combo." },
  { id: "AYkxxiNPJbY", title: "Fall Edition", emoji: "🍁", theme: "Fall", grade: "PreK-2", duration: "9 min",
    description: "Fall-themed cues — leaves, turkeys, harvest movements.",
    note: "Autumn transition warm-up — pair with a harvest character-focus discussion." },
  { id: "OrDuwHJP6-c", title: "Video Game Edition", emoji: "🎮", theme: "Games", grade: "PreK-2", duration: "12 min",
    description: "Video-game-themed locomotor cues that keep kids engaged.",
    note: "High-engagement pick — power-ups, jumps, dodges. Great indoor rescue day." },
  { id: "mh9gywQefyQ", title: "Valentines Day Edition", emoji: "❤️", theme: "Holidays", grade: "PreK-2", duration: "9 min",
    description: "Valentine-themed cues with a character-focus tie-in on kindness.",
    note: "February favorite — pair with kindness discussion + character-focus prompts." },
];
const DICE_VIDEOS = [
  { id: "zal3a6Dzhe4", title: "Superhero Dice Fitness Challenge", emoji: "🦸", theme: "Super Heroes", grade: "K-4", duration: "6 min",
    description: "Roll the dice, then do the superhero-themed exercise for that number.",
    note: "Hero-mindset warm-up. Each roll = a new challenge — no two classes look the same." },
  { id: "lLo2nan8fgg", title: "Dice Roll Fitness Activity", emoji: "🎲", theme: "Classic", grade: "K-4", duration: "5 min",
    description: "Standard dice fitness — 1-6 exercises for each roll.",
    note: "Great intro to dice fitness — teach the format, then let students take turns rolling." },
  { id: "YzfdGMpYmcI", title: "Dice Fitness Workout", emoji: "🎲", theme: "Classic", grade: "1-4", duration: "6 min",
    description: "Roll-and-move fitness routine with clear rep counts.",
    note: "Character focus: perseverance. Finish every rep before the next roll." },
  { id: "lS5OlT7eMYc", title: "Dice Fitness Challenge", emoji: "🎲", theme: "Classic", grade: "1-4", duration: "5 min",
    description: "Fast-paced dice fitness challenge for older elementary.",
    note: "Great small-group activity — split into teams, each team rolls together." },
  { id: "q7aQT-xepjI", title: "Large Group Warm Up", emoji: "🎲", theme: "Warm-up", grade: "K-4", duration: "6 min",
    description: "Dice fitness designed for a whole gym at once.",
    note: "Ideal for whole-class start-of-lesson warm-ups. Everyone rolls together." },
  { id: "j31WmA3tML4", title: "Back to School Dice Breaker", emoji: "🎒", theme: "School", grade: "K-4", duration: "6 min",
    description: "First-week icebreaker + fitness combo.",
    note: "Perfect Day-1 activity — students share a fact, then everyone does the exercise for their roll." },
  { id: "E7i_JDzejwc", title: "Roll the Dice Fitness", emoji: "🎲", theme: "Classic", grade: "K-4", duration: "5 min",
    description: "Cardio-focused dice fitness — great for get-your-heart-up days.",
    note: "Higher intensity — pair with water breaks. Fun on cardiovascular assessment days." },
  { id: "bYpSP0tP5ig", title: "Fit Dice Fitness", emoji: "💪", theme: "Fitness", grade: "1-4", duration: "6 min",
    description: "Fitness-focused rolls with strength + cardio blend.",
    note: "Balanced workout — pairs perfectly with FitnessGram prep weeks." },
];
const BB_THEMES_TOT = ["All","Animals","Sports","Super Heroes","Disney","Winter","Weather","School","Colors","Spring"];
const BB_THEMES_POKO = ["All","Fall","School","Movies","Super Heroes","Food","Holidays"];
const BB_THEMES_LLM = ["All","Original","Animals","Spring","Winter","Fall","Holidays","Games"];
const BB_THEMES_DICE = ["All","Super Heroes","Classic","Warm-up","School","Fitness"];
const BB_TABS = {
  tot:  { label: "This or That",             videos: () => TOT_VIDEOS,  themes: BB_THEMES_TOT,  tag: "This or That" },
  poko: { label: "Pick One Kick One",        videos: () => POKO_VIDEOS, themes: BB_THEMES_POKO, tag: "Pick One Kick One" },
  llm:  { label: "Locomotor Listen and Move",videos: () => LLM_VIDEOS,  themes: BB_THEMES_LLM,  tag: "Locomotor L&M" },
  dice: { label: "Dice Fitness",             videos: () => DICE_VIDEOS, themes: BB_THEMES_DICE, tag: "Dice Fitness" },
};

function BrainBreaks() {
  const [tab, setTab] = useState("tot");
  const [theme, setTheme] = useState("All");
  const [q, setQ] = useState("");
  useEffect(() => { setTheme("All"); }, [tab]);
  const current = BB_TABS[tab];
  const source = current.videos();
  const themes = current.themes;
  const filtered = useMemo(() => source.filter(v => {
    if (theme !== "All" && v.theme !== theme) return false;
    if (q && !(v.title + " " + v.theme + " " + v.note + " " + v.description).toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [source, theme, q]);
  return (
    <div>
      <section className="relative overflow-hidden border-b-4 border-black dark:border-white" style={{background:"var(--royal)"}}>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 text-white">
          <div className="inline-flex items-center gap-2 mb-3 chip bg-white text-black border-white"><Video size={14}/> BRAIN BREAK HUB</div>
          <h1 className="font-display font-black text-5xl sm:text-7xl uppercase leading-[0.9] tracking-tighter italic mb-3">
            Brain <span style={{color:"#FBBF24"}}>Breaks</span>
          </h1>
          <p className="text-lg sm:text-xl max-w-2xl font-bold">Quick-decision movement videos, listening drills, and dice fitness — four flavors for indoor rescue, transitions, and character-focus moments.</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Tab toggle */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex flex-wrap border-2 border-black dark:border-white" style={{background:"var(--surface)"}} role="tablist">
            {Object.entries(BB_TABS).map(([key, t], i) => (
              <button
                key={key}
                data-testid={`bb-tab-${key}`}
                role="tab"
                aria-selected={tab === key}
                onClick={()=>setTab(key)}
                className={`px-3 sm:px-4 py-2.5 font-display font-black uppercase tracking-wider text-[0.7rem] sm:text-sm transition-colors ${tab===key ? "text-white" : ""} ${i>0 ? "border-l-2 border-black dark:border-white" : ""}`}
                style={{background: tab===key ? "var(--royal)" : "transparent"}}
              >
                {t.label} <span className="chip !text-[0.6rem] !py-0 !px-1.5 ml-1" style={{background: tab===key?"#FBBF24":"var(--royal)", color: tab===key?"#000":"#fff", borderColor:"transparent"}}>{t.videos().length}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="card p-4 mb-6 space-y-3" style={{background:"var(--surface)"}}>
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2" style={{color:"var(--muted)"}}/>
            <input data-testid="bb-search" value={q} onChange={e=>setQ(e.target.value)} placeholder={`Search ${current.label} videos...`} className="input pl-10"/>
          </div>
          <div className="flex flex-wrap gap-2">
            {themes.map(t => (
              <button key={t} data-testid={`bb-theme-${t}`} onClick={()=>setTheme(t)} className={`chip cursor-pointer ${theme===t?"chip-blue":""}`}>{t}</button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-8" style={{color:"var(--muted)"}}>
            No videos yet for this theme.
            <a href={`https://www.youtube.com/results?search_query=${encodeURIComponent(current.label + " PE " + theme)}`} target="_blank" rel="noopener noreferrer" className="btn-sec !text-xs !py-1.5 !px-3 ml-2 inline-flex items-center gap-1">
              <ExternalLink size={12}/> Find on YouTube
            </a>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map(v => <VideoCard key={v.id} v={v} tag={current.tag}/>)}
          </div>
        )}
      </div>
    </div>
  );
}

function VideoCard({ v, tag }) {
  const { user, toggleFav } = useAuth();
  const favId = `video:${v.id}`;
  const isFav = user?.favorites?.includes(favId);
  return (
    <div className="card p-0 overflow-hidden" style={{background:"var(--surface)"}} data-testid={`bb-card-${v.id}`}>
      <div className="p-4 text-white flex items-start justify-between gap-2" style={{background:"var(--royal)"}}>
        <div className="min-w-0">
          <div className="flex flex-wrap gap-1 mb-2">
            {tag && <span className="chip bg-white text-black border-white !text-[0.6rem]">{tag}</span>}
            <span className="chip bg-white text-black border-white !text-[0.6rem]">{v.theme}</span>
            <span className="chip bg-white text-black border-white !text-[0.6rem]">{v.grade}</span>
            <span className="chip bg-white text-black border-white !text-[0.6rem]">{v.duration}</span>
          </div>
          <h3 className="font-display font-black text-xl uppercase leading-tight tracking-tight">
            <span className="mr-1">{v.emoji}</span>{v.title}
          </h3>
        </div>
        {user && (
          <button onClick={()=>toggleFav(favId)} data-testid={`fav-${favId}`} className="p-1 shrink-0" aria-label="favorite">
            <Heart size={18} fill={isFav?"#fff":"none"} strokeWidth={2.5}/>
          </button>
        )}
      </div>
      <div className="aspect-video bg-black">
        <iframe
          className="w-full h-full"
          src={`https://www.youtube-nocookie.com/embed/${v.id}?modestbranding=1&rel=0&playsinline=1`}
          title={v.title}
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
      <div className="p-4 text-sm space-y-3">
        {v.description && <p style={{color:"var(--muted)"}}>{v.description}</p>}
        <div>
          <div className="label mb-1" style={{color:"var(--royal)"}}>How to Play</div>
          <p style={{color:"var(--muted)"}}>{v.note}</p>
        </div>
        <a href={`https://www.youtube.com/watch?v=${v.id}`} target="_blank" rel="noopener noreferrer" data-testid={`bb-yt-${v.id}`} className="btn-sec !text-xs !py-1.5 !px-3 inline-flex items-center gap-1">
          <ExternalLink size={12}/> Open on YouTube
        </a>
      </div>
    </div>
  );
}

// Legacy redirect for old /this-or-that URL
function ThisOrThatRedirect() {
  const nav = useNavigate();
  useEffect(() => { nav("/brain-breaks", { replace: true }); }, [nav]);
  return null;
}

// ---------- ABOUT ----------
function About() {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-display font-black text-5xl uppercase tracking-tighter italic mb-3">About <span style={{color:"var(--royal)"}}>Panthers PE</span></h1>
      <p className="text-lg mb-6" style={{color:"var(--muted)"}}>Van Alstyne Panthers PE is a K-4 physical education hub built for elementary coaches — TEKS-aligned games, weather-smart planning, character focus, and championship-level curriculum.</p>
      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        {[
          ["📚","200+ Games","Every game is TEKS-aligned with a full 5E lesson plan and grade-specific modifications."],
          ["📅","31 Weekly Plans","Full Mon–Fri PE weeks with warm-ups, main activities, and character focus for the entire school year."],
          ["🌤️","Weather-Aware","Live Van Alstyne, TX conditions drive smart indoor/outdoor recommendations."],
          ["🤖","AI Coach","Generate a custom game or full week plan in seconds when you are stuck for ideas."],
          ["🎬","Brain Breaks","Curated This or That videos for quick indoor movement + decision-making."],
          ["🖨️","Print Anything","One-click printable packets — single games, full weeks, or multi-game bundles."],
        ].map(([e,t,d],i) => (
          <div key={i} className="card p-4" style={{background:"var(--surface)"}}>
            <div className="text-3xl mb-1">{e}</div>
            <div className="font-display font-black uppercase text-lg" style={{color:"var(--royal)"}}>{t}</div>
            <p className="text-sm mt-1">{d}</p>
          </div>
        ))}
      </div>
      <div className="card p-5" style={{background:"var(--surface)"}}>
        <h2 className="font-display font-black text-2xl uppercase mb-2" style={{color:"var(--royal)"}}>Built by coaches, for coaches</h2>
        <p className="text-sm mb-3">Panthers PE was created to give K-4 PE teachers everything they need in one place — no more digging through binders or old printouts to find the right game for today&apos;s weather, grade, and equipment.</p>
        <p className="text-sm">Questions, ideas, or want to add your own games? Contact your athletic coordinator or reach out directly.</p>
      </div>
    </div>
  );
}

// ---------- FOOTER ----------
function Footer() {
  return (
    <footer className="border-t-2 border-black dark:border-white mt-12 no-print" style={{background:"var(--surface)"}}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 grid md:grid-cols-3 gap-6">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="p-1.5 border-2" style={{background:"var(--royal)", color:"white", borderColor:"var(--text)"}}>
              <PawPrint strokeWidth={2.5} size={18}/>
            </div>
            <div className="font-display font-black text-xl italic">PANTHERS<span style={{color:"var(--royal)"}}>PE</span></div>
          </div>
          <p className="text-sm" style={{color:"var(--muted)"}}>Van Alstyne Panthers PE · Built by coaches, for coaches.</p>
        </div>
        <div>
          <div className="label mb-2" style={{color:"var(--royal)"}}>Explore</div>
          <div className="grid grid-cols-2 gap-y-1 text-sm">
            <Link to="/games" className="hover:underline">Games Library</Link>
            <Link to="/weeks" className="hover:underline">Weekly Lessons</Link>
            <Link to="/brain-breaks" className="hover:underline">Brain Breaks</Link>
            <Link to="/favorites" className="hover:underline">My Favorites</Link>
            <Link to="/ai" className="hover:underline">AI Coach</Link>
            <Link to="/weather" className="hover:underline">Weather</Link>
            <Link to="/about" className="hover:underline">About</Link>
            <Link to="/sections" className="hover:underline">All Sections</Link>
          </div>
        </div>
        <div>
          <div className="label mb-2" style={{color:"var(--royal)"}}>Contact</div>
          <p className="text-sm mb-1">Van Alstyne ISD · K-4 Physical Education</p>
          <p className="text-sm" style={{color:"var(--muted)"}}>Reach your athletic coordinator or PE lead for game submissions and feedback.</p>
        </div>
      </div>
      <div className="border-t py-3 px-6 text-xs text-center" style={{borderColor:"var(--border)", color:"var(--muted)"}}>
        © {new Date().getFullYear()} Van Alstyne Panthers PE. All content preserved from the original coaches&apos; curriculum.
      </div>
    </footer>
  );
}

// ---------- ROOT ----------
function Shell() {
  const [dark, setDark] = useTheme();
  return (
    <BrowserRouter>
      <GlobalSpotlight/>
      <Nav dark={dark} setDark={setDark}/>
      <Routes>
        <Route path="/" element={<Home/>}/>
        <Route path="/games" element={<Games/>}/>
        <Route path="/games/:id" element={<GameDetail/>}/>
        <Route path="/sections" element={<SectionsIndex/>}/>
        <Route path="/sections/:slug" element={<SectionPage/>}/>
        <Route path="/weeks" element={<WeeklyLessons/>}/>
        <Route path="/content/:id" element={<ContentPage/>}/>
        <Route path="/weather" element={<WeatherPage/>}/>
        <Route path="/ai" element={<AIGen/>}/>
        <Route path="/planner" element={<Planner/>}/>
        <Route path="/create" element={<CreateGame/>}/>
        <Route path="/favorites" element={<Favorites/>}/>
        <Route path="/this-or-that" element={<ThisOrThatRedirect/>}/>
        <Route path="/brain-breaks" element={<BrainBreaks/>}/>
        <Route path="/about" element={<About/>}/>
      </Routes>
      <Footer/>
    </BrowserRouter>
  );
}

export default function App() { return <AuthProvider><Shell/></AuthProvider>; }
